/**
 * ServiceInventoryPage.js — Phase A (2026-09-27)
 *
 * Products used IN-HOUSE during appointments — not the things you sell.
 *
 * PHASE A covers what the master bucket needs to be useful and verifiable on its own:
 * R1 enters what they bought, allocates it to one location or several at once, and every
 * change lands in an append-only history. Consumption and issuing to a professional are
 * Phase B, and this screen says so rather than showing an empty list that looks broken.
 *
 * WHAT THIS ROUTE USED TO BE: a redirect stub that bounced straight to Products. Three
 * routes have pointed here since the app was first wired, but the feature had no
 * implementation behind it — see docs/SERVICE_INVENTORY_PLAN.md §1. The redirect is what
 * made that impossible to see, so this page replaces it outright.
 *
 * ROLES: R1 creates items and allocates. R4/R7 own their own stock and get no allocation
 * step at all — their stock is theirs and the shop's is not. R2/R3 read what is at their
 * own location; the SERVER enforces that scoping, this screen only reflects it.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import SidebarLayout from "../shops/SidebarLayout";
import StaffSidebarLayout from "./StaffSidebarLayout";
import FreelancerSidebarLayout from "../freelancer/FreelancerSidebarLayout";
import "./ServiceInventoryPage.css";

// The same Chart.js setup the analytics pages use, so a reader moving between them sees the same
// tooltips, the same scales and the same colour behaviour.
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// One palette for every chart here. Money and stock are read the same way twice, so they must not
// be drawn in two different colours in two different places.
const CHART_COLOURS = {
  cost: "#2563eb",
  ready: "#059669",
  withStaff: "#f59e0b",
  // Spent, not held. Deliberately unlike the other two — this bar never adds up with them.
  used: "#94a3b8",
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UNITS = [
  { value: "each", label: "each — whole items (bottles, foils, gloves)" },
  { value: "ml", label: "ml — millilitres, poured or dispensed" },
  { value: "g", label: "g — grams" },
  { value: "oz", label: "oz — ounces" },
];

const UNIT_SUFFIX = { each: "each", ml: "ml", g: "g", oz: "oz" };

const MOVEMENT_LABELS = {
  receive: "Added to storeroom",
  allocate: "Sent to a shop",
  issue: "Given to a stylist",
  return: "Handed back",
  consume: "Used on a client",
  // NOT "used". Waste and consumption are separate numbers, and the whole reason they are
  // separate is so a report can say what product cost versus what went missing. A label
  // that reads "used / wasted" puts them back together in the one place people look.
  waste: "Spilled or expired",
  adjustment: "Count corrected",
};

/**
 * Periods the cost report can be asked for.
 *
 * "This month" first and default, because that is the number a shop owner checks — a
 * report that opens on "all time" answers a question nobody asked and hides the one they
 * did.
 */
const COST_PERIODS = [
  { id: "this-month", label: "This month" },
  { id: "last-month", label: "Last month" },
  { id: "90d", label: "Last 90 days" },
  { id: "this-year", label: "This year" },
];

/**
 * HOW LONG ONE CONTAINER SHOULD LAST — answered once per product.
 *
 * A container's cost is divided when it is FINISHED, so one that is never finished divides nothing:
 * its money lands on no service. That is why the age of an open container is worth saying out loud.
 *
 * But "too long" is not one number. A litre of developer is spent in a fortnight; a tub of talcum
 * powder can sit open for a year, and a fixed month would nag about it — which is how a warning
 * becomes noise, and then becomes invisible. So the answer lives on the PRODUCT, where the fact is,
 * and every container of that product uses it.
 *
 * Phrased as a question about the product rather than a number of days, because "how long should one
 * last" is answerable by the person doing the buying and "openTooLongDays" is not. **The last option
 * is a real answer, not an escape hatch**: products with no meaningful life should not be guessed at.
 */
const OPEN_TOO_LONG_CHOICES = [
  { id: "30", label: "About a month", hint: "A developer, a colour tube" },
  { id: "90", label: "About 3 months", hint: "Most bulk liquids" },
  { id: "180", label: "About 6 months", hint: "Slow sellers" },
  { id: "365", label: "Up to a year", hint: "Large tubs and powders" },
  { id: "0", label: "No reminder", hint: "It lasts as long as it lasts" },
];

function periodRange(id) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  if (id === "last-month") {
    return {
      from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
      // One millisecond before this month began, so no movement falls in both periods.
      to: new Date(monthStart.getTime() - 1),
    };
  }
  if (id === "this-year") {
    return { from: new Date(now.getFullYear(), 0, 1), to: now };
  }
  if (id === "90d") {
    return { from: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000), to: now };
  }
  return { from: monthStart, to: now };
}

function authHeaders() {
  const token = localStorage.getItem("ogestyle_token");
  return { Authorization: `Bearer ${token}` };
}

function formatQty(quantity, unit) {
  const value = Number(quantity) || 0;
  // Countable things never show a decimal; measured things drop trailing zeros so
  // "500" does not read as "500.000".
  if (unit === "each") return String(Math.round(value));
  return String(Math.round(value * 1000) / 1000);
}

/**
 * "1.2 bottles" — pack size is display-only, so this never feeds a calculation. It exists
 * because nobody at a salon thinks in millilitres.
 */
function formatPacks(quantity, unit, packSize) {
  if (!packSize || packSize <= 0) return null;
  const packs = (Number(quantity) || 0) / packSize;
  if (!Number.isFinite(packs)) return null;
  const rounded = Math.round(packs * 100) / 100;
  return `${rounded} × ${formatQty(packSize, unit)} ${UNIT_SUFFIX[unit] || unit}`;
}

function money(value) {
  const n = Number(value) || 0;
  return n ? `$${n.toFixed(2)}` : "—";
}

/**
 * WHERE SOMETHING IS: "City, ST".
 *
 * Two changes are folded into this one function, and the second is the one that matters.
 *
 * 1. THE NAME IS GONE. It used to be "Name, City, ST", on the reasoning that duplicate names must
 *    never be ambiguous. The owner overruled it and he is right for this data: a business running
 *    three shops calls them all the same thing, so the NAME is the part that repeats and the city
 *    is the part that tells them apart. "Mo's Beauty World, Fox River Grove, IL" is longer, says
 *    the same thing three times, and still needs the last two words to be useful. The name stays
 *    as the fallback, because a mobile or online location has nothing else to be called.
 *
 * 2. IT ACCEPTS BOTH SHAPES, because there ARE two and picking the wrong one fails SILENTLY. A
 *    populated `ShopLocation` carries `name` / `city` / `state`; a denormalised row — a movement, a
 *    stock row, a holding, a container — carries `locationName` / `locationCity` / `locationState`
 *    AND a `name` of its own, which is the PRODUCT. Reading `.name` off the second shape does not
 *    throw, it returns the product, so the mistake appears as a shop called "Detox" rather than as
 *    an error. That has now happened TWICE, in two different screens.
 *
 *    So `locationName` is read FIRST. It only exists on the denormalised shape, which is exactly
 *    the case that used to go wrong.
 */
function locationLabel(location) {
  if (!location) return "—";
  const city = location.locationCity || location.city;
  const state = location.locationState || location.state;
  const name = location.locationName || location.name;
  return [city, state].filter(Boolean).join(", ") || name || "—";
}

function roleFromStorage() {
  const roles = JSON.parse(localStorage.getItem("ogestyle_roles") || "[]");
  return (
    localStorage.getItem("ogestyle_role_override") ||
    localStorage.getItem("ogestyle_role") ||
    (Array.isArray(roles) && roles.length > 0 ? roles[0] : "")
  );
}

/**
 * BULK IMPORT — parse CSV in the browser, then create the items through the EXISTING endpoint.
 *
 * No import endpoint, deliberately. `POST /items` already validates the unit and the cost, creates
 * the item, and writes any opening stock through the ledger as a real movement. A second path that
 * accepted an array would be a second place for those rules to live, and the two would drift —
 * the import would eventually accept something the form refuses, or skip the opening movement and
 * leave a balance with no history behind it.
 *
 * A header is REQUIRED, and matched loosely: a spreadsheet exported under "Stock Unit" and one
 * typed as "stock_unit" are the same column, and asking somebody to rename headers is how a bulk
 * import goes unused.
 */
const IMPORT_COLUMNS = {
  name: ["name", "product", "item", "product name"],
  unit: ["unit", "stock unit", "measure", "uom"],
  unitCost: ["unit cost", "unit cost", "cost", "price", "unit price"],
  category: ["category", "type", "group"],
  supplierName: ["supplier", "supplier name", "vendor"],
  lowStockThreshold: ["low stock", "low stock threshold", "threshold", "warn below", "reorder at"],
  openTooLongDays: [
    "lasts days",
    "lasts",
    "shelf life",
    "how long it lasts",
    "open too long",
    "lasts (days)",
  ],
  initialQuantity: ["quantity", "qty", "opening quantity", "opening", "stock", "amount"],
  usageMode: ["usage mode", "how used", "used", "shared"],
};

const IMPORT_UNITS = {
  each: "each",
  item: "each",
  items: "each",
  bottle: "each",
  bottles: "each",
  box: "each",
  boxes: "each",
  packet: "each",
  pack: "each",
  ml: "ml",
  millilitre: "ml",
  millilitres: "ml",
  milliliter: "ml",
  milliliters: "ml",
  g: "g",
  gram: "g",
  grams: "g",
  oz: "oz",
  ounce: "oz",
  ounces: "oz",
};

const IMPORT_USAGE_MODES = {
  shared: "shared",
  "shared across appointments": "shared",
  "shared across jobs": "shared",
  bottle: "shared",
  container: "shared",
  "per appointment": "per_appointment",
  "per_appointment": "per_appointment",
  "per job": "per_appointment",
  once: "per_appointment",
  "once per appointment": "per_appointment",
  each: "per_appointment",
};

/**
 * THE IMPORT TEMPLATE IS ONE LIST, USED THREE WAYS: the placeholder, the "use an example"
 * button, and the file that "Download template" hands over. Three copies of the same sample rows
 * would be three things to keep in step, and the one a user downloads is the one nobody edits.
 */
const IMPORT_EXAMPLE_ROWS = [
  "name,unit,unit cost,category,supplier,low stock,quantity,usage mode,lasts days",
  "Developer 20 vol,ml,12.50,Colour,Salon Supplies,1000,1000,once per appointment,",
  "Cotton wool pads,each,4.20,Consumable,Salon Supplies,20,120,once per appointment,",
  "Bleach powder,g,18.00,Colour,Salon Supplies,500,750,shared,90",
  "Talcum powder,g,9.00,Consumable,Salon Supplies,200,500,shared,365",
];

/**
 * A COLUMN REFERENCE A PERSON CAN SCAN, instead of a paragraph they have to parse.
 * The modal used to open with eight lines of grey prose describing the columns; the same
 * information as a three-column table is read at a glance and reads as instructions rather
 * than as an essay.
 */
const IMPORT_COLUMN_HELP = [
  { column: "name", required: true, what: "The product", example: "Developer 20 vol" },
  { column: "unit", what: "each, ml, g or oz — how it is measured", example: "ml" },
  { column: "unit cost", what: "What you paid per unit", example: "12.50" },
  { column: "quantity", what: "How much you have now", example: "1000" },
  { column: "low stock", what: "Warn below this much", example: "500" },
  {
    column: "usage mode",
    what: "once per appointment, or shared for one container across clients",
    example: "shared",
  },
  {
    column: "lasts days",
    what: "Shared only — how long one container lasts. Blank = about a month. 0 = never remind",
    example: "365",
  },
  { column: "category, supplier", what: "Optional", example: "Colour" },
];

/** Comma-separated with double quotes. Returns trimmed cells. */
function splitCsvLine(line) {
  const values = [];
  let current = "";
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (quoted) {
      if (char === '"') {
        if (line[index + 1] === '"') {
          current += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current.trim());
  return values;
}

const normaliseHeader = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Turn pasted text into rows, each carrying its own errors.
 *
 * NOTHING IS REJECTED WHOLESALE. One bad line is reported on that line and the rest still import;
 * refusing the whole file because row 47 has a typo is how somebody gives up and types 60 products
 * by hand instead.
 */
function parseImportCsv(text) {
  const lines = String(text || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .filter((line) => line.trim().length > 0);

  if (lines.length < 2) {
    return { rows: [], fatal: "Paste a header row and at least one product." };
  }

  const headers = splitCsvLine(lines[0]).map(normaliseHeader);
  const indexOf = {};
  for (const [field, aliases] of Object.entries(IMPORT_COLUMNS)) {
    const at = headers.findIndex((header) => aliases.includes(header));
    if (at >= 0) indexOf[field] = at;
  }

  if (indexOf.name === undefined) {
    return {
      rows: [],
      fatal:
        "No “name” column found. The first row must be headings — at minimum: name, unit, unit cost, quantity.",
    };
  }

  const rows = lines.slice(1).map((line, position) => {
    const cells = splitCsvLine(line);
    const read = (field) => (indexOf[field] === undefined ? "" : cells[indexOf[field]] || "");

    /**
     * A NUMBER, OR A COMPLAINT — never a silent zero.
     *
     * Stripping "$" and thousands separators is a kindness. Stripping LETTERS is not: "ten" and
     * "TBC" both reduce to the empty string, and `Number("")` is 0, which is finite — so a cost
     * column full of "TBC" would have imported cleanly as a shelf of free products and quietly
     * skewed every cost-per-service figure after it. A number with no digit in it is a typo, and
     * the row says so.
     */
    const readNumber = (raw) => {
      const text = String(raw == null ? "" : raw).trim();
      if (text === "") return { value: null, ok: true }; // absent — the caller's default applies
      if (!/[0-9]/.test(text)) return { value: NaN, ok: false };
      const value = Number(text.replace(/[^0-9.-]/g, ""));
      return { value, ok: Number.isFinite(value) };
    };

    const errors = [];
    const name = read("name");
    if (!name) errors.push("no name");

    const rawUnit = normaliseHeader(read("unit")) || (read("unit") ? "" : "each");
    const unit = IMPORT_UNITS[rawUnit];
    if (!unit) {
      errors.push(`unit “${read("unit")}” is not one of each, ml, g, oz`);
    }

    const rawCost = read("unitCost");
    const cost = readNumber(rawCost);
    if (!cost.ok) errors.push(`cost “${rawCost}” is not a number`);
    const unitCost = cost.value === null ? 0 : cost.value;

    const rawQty = read("initialQuantity");
    const qty = readNumber(rawQty);
    if (!qty.ok) errors.push(`quantity “${rawQty}” is not a number`);
    const quantity = qty.value === null ? 0 : qty.value;
    if (unit === "each" && quantity && !Number.isInteger(quantity)) {
      // Caught here so the row fails in the preview rather than half-way through an import.
      errors.push("a product counted in whole units cannot have a fractional quantity");
    }

    const rawThreshold = read("lowStockThreshold");
    const threshold = readNumber(rawThreshold);
    if (!threshold.ok) errors.push(`low stock “${rawThreshold}” is not a number`);

    const rawMode = normaliseHeader(read("usageMode"));
    const usageMode = rawMode ? IMPORT_USAGE_MODES[rawMode] : "per_appointment";
    if (rawMode && !usageMode) {
      errors.push(`usage “${read("usageMode")}” is not “shared” or “once per appointment”`);
    }

    /*
      HOW LONG ONE LASTS. Left blank it takes the product default (a month), which is the right
      answer for most of a catalogue — so a file that says nothing about it still imports cleanly.
      Present so that a big catalogue of SHARED products does not have to be opened one at a time to
      stop the screen nagging about a tub of powder.
    */
    const rawOpenTooLong = read("openTooLongDays");
    const openTooLong = readNumber(rawOpenTooLong);
    if (!openTooLong.ok) errors.push(`how long it lasts “${rawOpenTooLong}” is not a number`);
    if (openTooLong.value !== null && openTooLong.value < 0) {
      errors.push("how long it lasts cannot be negative");
    }

    return {
      line: position + 2, // the spreadsheet row, so the message matches what they see
      name,
      unit: unit || "each",
      unitCost,
      category: read("category"),
      supplierName: read("supplierName"),
      lowStockThreshold: threshold.value === null ? 0 : threshold.value,
      openTooLongDays: openTooLong.value === null ? undefined : openTooLong.value,
      initialQuantity: quantity,
      usageMode: usageMode || "per_appointment",
      errors,
    };
  });

  return { rows, fatal: null };
}

/**
 * The page renders INSIDE its role's sidebar layout, like every other dashboard page.
 *
 * Three families, because a sidebar is a promise about where you can go:
 *
 *   R3        -> StaffSidebarLayout, the SAME one every other /dashboard/professional/*
 *                page uses. It used to fall through to the business sidebar, which both
 *                looked wrong and left the professional with no route BACK to this page:
 *                "My Service Products" lives in the staff sidebar, and the business one has
 *                no such entry. One page rendering a different shell from its siblings is
 *                how you end up able to reach a screen but not leave it.
 *   R4/R7     -> FreelancerSidebarLayout. NOT RoleAwareLayout: that derives the user type
 *                from the URL, and an R7 sitting on /dashboard/booth-renter/* falls through
 *                to `business` — which would hand a booth renter the shop's sidebar.
 *   R1/R2     -> the business sidebar, which is where their inventory entry lives.
 */
function PageShell({ role, children }) {
  const isIndependent = role === "R4" || role === "R7";
  if (isIndependent) {
    return <FreelancerSidebarLayout>{children}</FreelancerSidebarLayout>;
  }
  if (role === "R3") {
    return (
      <StaffSidebarLayout roleType="professional">{children}</StaffSidebarLayout>
    );
  }
  return <SidebarLayout>{children}</SidebarLayout>;
}

function ServiceInventoryPage({ view = "stock" }) {
  const role = useMemo(() => roleFromStorage(), []);
  const isIndependent = role === "R4" || role === "R7";
  // R3 delivers the service but owns no stock: their page is the "given to me" record.
  const isProfessional = role === "R3";
  const canManage = role === "R1" || isIndependent;
  const canAllocate = role === "R1";
  /**
   * MAY SEE WHAT HAS GONE OUT, WITHOUT SEEING WHAT IT COST.
   *
   * `canManage` is who BEARS the cost, and `/cost-report` belongs to them because it carries
   * `unitCost` — what the business paid.
   *
   * An R2 runs the shop and needs the same at-a-glance answer to "is this going down" that the
   * owner gets, but not the buying price. So they are given a separate, money-free feed
   * (`/usage-summary`) for the chart's third bar: the same picture with one fewer fact in it.
   *
   * The permission is SPLIT rather than shared because the two questions really are different —
   * "what is happening in my shop" and "what did I spend" — and collapsing them is how a role
   * that may look gets handed a figure that was never theirs.
   */
  const canSeeUsage = canManage || role === "R2";

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [stock, setStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    name: "",
    unit: "ml",
    packSize: "",
    unitCost: "",
    category: "",
    supplierName: "",
    lowStockThreshold: "",
    openTooLongDays: "30",
    notes: "",
    initialQuantity: "",
    // The safe default: a product is assumed to be used once per appointment unless the owner
    // says otherwise. Treating a 1:1 product as shared would spread its cost over jobs that
    // never touched it.
    usageMode: "per_appointment",
  });
  const [busy, setBusy] = useState(false);

  const [allocateItem, setAllocateItem] = useState(null);
  const [allocateLocationIds, setAllocateLocationIds] = useState([]);
  const [allocateQty, setAllocateQty] = useState("");
  const [allocateNotes, setAllocateNotes] = useState("");

  // Phase B — the "record what was used" action, and the owner's approval queue.
  const [useItem, setUseItem] = useState(null);
  const [useQty, setUseQty] = useState("");
  const [useReason, setUseReason] = useState("");
  // Which of the three things this modal is about to do. Previously each footer button acted
  // immediately on click, so "Correct the count" submitted a count correction the moment you
  // pressed it — before you had said what the count was.
  const [useMode, setUseMode] = useState("consume");

  // Product cost — the figure the ledger exists to make possible. Owner-visible only: it is
  // buying information, and this page is shared with shop admins.
  const [costPeriod, setCostPeriod] = useState("this-month");
  const [costReport, setCostReport] = useState(null);
  // Quantities only, for the callers who may see use but not spend. Never both at once:
  // `costReport` for the owner and the independent, `usageSummary` for a shop admin.
  const [usageSummary, setUsageSummary] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  // Bumped by `load()`, so recording stock refreshes the report with it rather than leaving
  // a total that disagrees with the table underneath.
  const [costRefresh, setCostRefresh] = useState(0);

  // Handing stock to a named professional.
  const [issueItem, setIssueItem] = useState(null);
  const [issueStaffId, setIssueStaffId] = useState("");
  const [issueQty, setIssueQty] = useState("");
  const [issueNotes, setIssueNotes] = useState("");
  const [staffOptions, setStaffOptions] = useState([]);
  const [adjustments, setAdjustments] = useState([]);

  /**
   * SHARED CONTAINERS — opened once, used across many appointments.
   *
   * Their cost is never divided while open: only the list of appointments they served is
   * recorded, and the division happens at finish when the divisor has become a fact rather than
   * a guess. So the panel shows the COUNT and the resulting share, never asks for an amount.
   */
  const [bottles, setBottles] = useState([]);
  const [bottleSummary, setBottleSummary] = useState(null);
  const [bottlePanel, setBottlePanel] = useState(null);
  const [bottleSaving, setBottleSaving] = useState(false);

  // Stock that has left a shelf but is not used yet. Separate from `stock` because the shelf
  // figure alone stops matching the cupboard the moment anything is issued.
  const [outstanding, setOutstanding] = useState([]);
  const [outstandingTotals, setOutstandingTotals] = useState(null);

  /**
   * RECORDING USAGE AGAINST AN APPOINTMENT.
   *
   * This is what makes "what did product cost me per service" answerable at all: without an
   * appointment on the movement, consumption is a quantity with a date and no reason, and the
   * only average anybody could compute is spend divided by a period.
   */
  const [apptUse, setApptUse] = useState(null); // { locationId, appointmentRefs[], sharedServiceId }
  const [apptOptions, setApptOptions] = useState([]);
  const [apptLines, setApptLines] = useState({});
  const [apptDefaults, setApptDefaults] = useState([]);
  const [apptLoading, setApptLoading] = useState(false);

  /*
    A FINISHED CONTAINER OPENS READ-ONLY.

    Its figures are already in the month's costing, and a tick list that looks live invites a stray
    click that silently re-divides a period that has been reported on. So the list is shown as the
    record it is, and changing it is an explicit act: press Update. The ticks still save the moment
    they are made — this gates the FIRST edit, not the saving of it.
  */
  const [bottleEditing, setBottleEditing] = useState(false);

  /*
    THE JOBS ALREADY ON THIS CONTAINER, WHETHER OR NOT THEY ARE IN THE WINDOW.

    The tick list above is a window over the appointment book, so anything older than it was counted
    but not shown — which meant an old container could not be corrected at all, the one thing this
    panel is for. These rows are fetched by booking id, so they are always listed and always
    untickable, and a container whose jobs have aged out is no longer a dead end.
  */
  const [coveredAppointments, setCoveredAppointments] = useState([]);

  // "I have bought some of this." The missing half of the original design: without it the
  // bucket that allocation draws FROM had no balance to draw on.
  const [receiveItem, setReceiveItem] = useState(null);
  const [receiveQty, setReceiveQty] = useState("");
  // Only offered when the business has ONE location — see `receiveStock`.
  const [receiveSendToLocation, setReceiveSendToLocation] = useState(true);

  // What has been handed to THIS person. Fetched for anyone with a staff record, because
  // the whole point is that being named against stock must be checkable by the person
  // named. An owner sees their own (usually empty) list, which is harmless.
  const [myHoldings, setMyHoldings] = useState([]);
  const [myMovements, setMyMovements] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  /**
   * THE FIRST-RUN GUIDE.
   *
   * Shown only while there is genuinely nothing here — the state in which a new owner opens the
   * page, sees four tiles reading zero and an empty table, and has no idea that the first move is
   * "add a product", not "add stock". It disappears on its own the moment they add one, and the
   * dismissal is remembered so it cannot become permanent furniture.
   *
   * This is the ONE place on this page where a paragraph is the right answer: every other
   * explanation is about a number the reader can already see, and this one is about a number that
   * does not exist yet.
   */
  const [guideDismissed, setGuideDismissed] = useState(
    () => localStorage.getItem("ogestyle_inventory_guide_dismissed") === "1",
  );

  /**
   * ONE PRODUCT, IN FULL.
   *
   * The list is for scanning; this is for looking at a product. Everything that used to be
   * crammed onto a table row — where it is, who has it, which services use it, what has happened
   * to it, and every action you can take on it — lives here instead, fetched when it is opened.
   *
   * Fetched rather than derived, because the history is per-item and already exists as an
   * endpoint (`/movements?itemId=`) that applies the caller's own scope. Making the detail view
   * assemble its own history from the page's 50 most recent movements would show a product whose
   * last use was 60 movements ago as having no history at all.
   */
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailHistory, setDetailHistory] = useState([]);

  /** One service's cost, opened up. Holds the `byService` row, whose `products[]` explains it. */
  const [serviceDetail, setServiceDetail] = useState(null);

  /**
   * Attributing a usage entry to the client it was really for.
   *
   * `attributeTarget` is the unattributed usage being explained; the options are the business's
   * recent appointments near that date. Business-wide on purpose — an owner attributing a
   * stylist's usage cannot do it from their OWN appointment list, which is the mistake a narrower
   * query would have made.
   */
  const [attributeTarget, setAttributeTarget] = useState(null);
  const [attributeOptions, setAttributeOptions] = useState([]);
  const [attributeRef, setAttributeRef] = useState("");
  const [attributeLoading, setAttributeLoading] = useState(false);
  const [attributeSaving, setAttributeSaving] = useState(false);

  // Bulk import. `importText` is the raw paste; `importPreview` is the parsed result, recomputed
  // on every keystroke so the table below the box is always telling the truth about what the
  // button would do.
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importPreview, setImportPreview] = useState({ rows: [], fatal: null });
  const [importBusy, setImportBusy] = useState(false);
  const [importOutcome, setImportOutcome] = useState(null);

  const canAdjust = role === "R1" || role === "R2" || isIndependent;
  const canApprove = role === "R1" || isIndependent;
  // Handing stock to a NAMED person. R1 and R2 — the two roles that control a location's
  // shelf — and it names somebody, which is what makes it accountable.
  const canIssue = role === "R1" || role === "R2";
  /**
   * WHO MAY RECORD USAGE. Mirrors `mayRecordUsage` on the server, and the server is the
   * authority — this copy exists only so the page does not offer a button that will 403.
   *
   * An R2 does not deliver services, so a usage entry they create records nothing that
   * happened: it is stock nobody can account for wearing a label that looks fine. Their stock
   * action is issuing to a named professional, which is checkable by the person named.
   */
  /**
   * TWO DIFFERENT CAPABILITIES, and they were one flag doing both jobs.
   *
   * `canRecordUsage` — may say WHAT A CLIENT USED. The professional, and only the professional:
   * a service consumes product, so the person who delivered it is the only person who can say. **A
   * one-man shop loses nothing by this**, because that owner also holds an R3 profile and records
   * as themselves-as-professional — the same way they deliver the service. What they cannot do is
   * record it while sitting in the owner profile, which is the desk asserting what happened in a
   * room it was not in.
   *
   * `canSeeContainers` — may LOOK at the containers. An owner must, because the money is theirs and
   * an open container that nobody finishes charges nothing to anybody; the alert about those is the
   * whole reason the section exists. They look and do not touch — see `maySetContainerClients`.
   *
   * Collapsing these into one flag is what removed the container list from the owner's screen, and
   * with it the only warning that money was quietly going nowhere.
   */
  const canRecordUsage = isProfessional || isIndependent;
  const canSeeContainers = role === "R1" || role === "R2" || canRecordUsage;

  /**
   * TRUE when the action modal was opened from a holding the caller was given.
   *
   * Only a holding can be HANDED BACK — you cannot return stock you never took — so the
   * "hand back" choice is offered there and nowhere else. Carried on the item rather than
   * inferred, because the same modal serves the shelf, the catalogue and the holding list.
   */
  const useFromHolding = Boolean(useItem?.fromHolding);

  /**
   * COUNT-ONLY mode for the action modal.
   *
   * The master inventory is a store, so only its count can be wrong there. And an R2 never
   * records usage at all — service is not theirs to record — so their only stock action is a
   * count correction, which goes to the owner. An independent professional must NOT be caught
   * by either branch: their `locationId: null` balance is the shelf they work from, not a
   * store.
   */
  const useIsCountOnly =
    Boolean(useItem) && ((useItem.locationId === null && !isIndependent) || !canRecordUsage);

  const load = useCallback(async () => {
    setError("");
    try {
      const headers = authHeaders();
      const requests = [
        axios.get(`${API_BASE_URL}/api/service-inventory/summary`, { headers }),
        axios.get(
          `${API_BASE_URL}/api/service-inventory/items${
            showArchived ? "?includeInactive=true" : ""
          }`,
          { headers },
        ),
        axios.get(`${API_BASE_URL}/api/service-inventory/stock`, { headers }),
        axios.get(`${API_BASE_URL}/api/service-inventory/movements?limit=25`, {
          headers,
        }),
      ];
      const [summaryRes, itemsRes, stockRes, movementsRes] = await Promise.all(
        requests,
      );
      setSummary(summaryRes.data || null);
      setItems(itemsRes.data?.items || []);
      setStock(stockRes.data?.stock || []);
      setMovements(movementsRes.data?.movements || []);

      // "What am I holding?" — scoped to the caller's own staff record server-side, so this
      // cannot read anyone else's holdings even if the id were tampered with.
      try {
        const res = await axios.get(`${API_BASE_URL}/api/service-inventory/mine`, {
          headers,
        });
        setMyHoldings(res.data?.holdings || []);
        setMyMovements(res.data?.movements || []);
      } catch {
        setMyHoldings([]);
        setMyMovements([]);
      }

      // The approval queue is owner-only, so it is fetched separately rather than
      // producing a guaranteed 403 in the batch above for everyone else.
      if (canApprove) {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/api/service-inventory/adjustments`,
            { headers },
          );
          setAdjustments(res.data?.adjustments || []);
        } catch {
          setAdjustments([]);
        }
      }

      // The cost figure is a summary of the movements just fetched, so it is refreshed with
      // them. Leaving it stale would print a total that disagrees with the table directly
      // above it — the one thing a report about money must never do.
      setCostRefresh((n) => n + 1);

      // What has been handed out and not yet used. Only R1 and R2 ever issue stock, so only
      // they have a reason to reconcile the shelf against the cupboard.
      if (canIssue) {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/api/service-inventory/outstanding`,
            { headers },
          );
          setOutstanding(res.data?.outstanding || []);
          setOutstandingTotals(res.data?.totals || null);
        } catch {
          setOutstanding([]);
          setOutstandingTotals(null);
        }
      }

      // Open containers. Fetched by anyone who may LOOK at one, not only by whoever may act:
      // an open container the owner cannot see is one nobody finishes, and a container nobody
      // finishes is stock that never leaves the books and money that lands on no service.
      if (canSeeContainers) {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/api/service-inventory/bottles`,
            { params: { status: "all" }, headers },
          );
          setBottles(res.data?.bottles || []);
          setBottleSummary(res.data?.summary || null);
        } catch {
          setBottles([]);
          setBottleSummary(null);
        }
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to load your service inventory.",
      );
    } finally {
      setLoading(false);
    }
  }, [canApprove, canIssue, canSeeContainers, showArchived]);

  /**
   * Approve or reject a pending adjustment. Approving is what finally moves the balance —
   * until then nothing has changed, which is the entire point of the approval gate.
   */
  const decideAdjustment = useCallback(
    async (movementId, approve) => {
      setBusy(true);
      setError("");
      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/service-inventory/adjustments/${movementId}/decide`,
          { approve },
          { headers: authHeaders() },
        );
        setNotice(res.data?.message || (approve ? "Approved." : "Rejected."));
        await load();
      } catch (err) {
        setError(err?.response?.data?.message || "Unable to record your decision.");
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  /**
   * Record stock used from a location. Which movement this becomes is NOT cosmetic:
   *  - "Used in a service" consumes stock at the cost of delivering it
   *  - "Lost or spoiled" is a loss, and needs a reason because "waste" is the easiest
   *    label to hide a loss behind
   */
  const submitUse = async (event) => {
    event.preventDefault();
    if (!useItem) return;
    /**
     * ENFORCED HERE, not just hidden by the choices above.
     *
     * `locationId: null` on BUSINESS-owned stock is the master inventory — the store you send
     * stock out of. Nobody performs a service out of a store, so no service can consume it:
     * usage belongs at the location where the service actually happened. The only thing that
     * can be true of the store's number is that it is wrong.
     *
     * An independent's `locationId: null` is their own shelf, not a store, so this must not
     * catch them — which is why the flag checks `!isIndependent` rather than the location alone.
     */
    if (useIsCountOnly && useMode !== "adjust") {
      setError(
        canRecordUsage
          ? "Usage is recorded where the work happens — at a shop. The storeroom is where you keep stock, so only its count can be corrected here."
          : "Usage is recorded by the professional who delivered the service. From here you can correct the count, issue stock to a professional, or ask the owner to review a change.",
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      const base = `${API_BASE_URL}/api/service-inventory`;
      const headers = authHeaders();

      if (useMode === "adjust") {
        /**
         * A CORRECTION TAKES THE SHELF'S REAL COUNT, and the computer does the subtraction.
         *
         * The field used to take a positive delta, which meant a count that had drifted HIGH
         * could not be brought down at all — the one direction you need a correction for was
         * the direction the form refused. Asking for the number actually on the shelf removes
         * the sign question from whoever is holding the bottle.
         */
        const counted = Number(useQty);
        const delta = Math.round((counted - Number(useItem.quantity)) * 1000) / 1000;
        if (!Number.isFinite(counted)) {
          setError("Enter the number you actually counted.");
          return;
        }
        if (delta === 0) {
          setError(
            `That is already the recorded count — nothing to correct. Change the number if the shelf disagrees.`,
          );
          return;
        }
        const res = await axios.post(
          `${base}/adjust`,
          {
            itemId: useItem.itemId,
            locationId: useItem.locationId,
            quantity: delta,
            reason: useReason || "Stock correction",
          },
          { headers },
        );
        setNotice(res.data?.message || "Adjustment recorded.");
      } else if (useMode === "consume") {
        // `consume` and `waste` are SEPARATE on purpose — "used on a customer" and "lost"
        // are different numbers, and a report that lumps them together can never tell you
        // your product cost. So this posts to /consume, not /waste.
        const res = await axios.post(
          `${base}/consume`,
          {
            locationId: useItem.locationId,
            lines: [
              {
                itemId: useItem.itemId,
                quantity: Math.abs(Number(useQty)),
                note: useReason || undefined,
              },
            ],
          },
          { headers },
        );
        setNotice(res.data?.message || "Recorded as used.");
      } else if (useMode === "return") {
        // CREDITS the location, so the server checks the holding first: handing back more than
        // was taken would be inventing stock, and an increase has no balance to be conditional
        // on — the one direction the ledger cannot guard by arithmetic alone.
        const res = await axios.post(
          `${base}/return`,
          {
            itemId: useItem.itemId,
            locationId: useItem.locationId,
            quantity: Math.abs(Number(useQty)),
            reason: useReason || undefined,
          },
          { headers },
        );
        setNotice(res.data?.message || "Returned to the shelf.");
      } else {
        const res = await axios.post(
          `${base}/waste`,
          {
            itemId: useItem.itemId,
            locationId: useItem.locationId,
            quantity: Math.abs(Number(useQty)),
            wastedByStaffId: useItem.staffId || undefined,
            reason: useReason || "Spoiled",
          },
          { headers },
        );
        setNotice(res.data?.message || "Recorded.");
      }

      setUseItem(null);
      setUseQty("");
      setUseReason("");
      setUseMode("consume");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to record this.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  // Only R1 has more than one location to allocate to, so only R1 needs the list.
  useEffect(() => {
    if (!canAllocate) return;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, {
          headers: authHeaders(),
        });
        const shops = res.data?.shops;
        setLocations(Array.isArray(shops) ? shops : []);
      } catch {
        setLocations([]);
      }
    })();
  }, [canAllocate]);

  /**
   * Product cost for the chosen period.
   *
   * Fetched only when the caller is allowed to see it, rather than fetched-and-hidden: an
   * endpoint that returns buying prices to a request the page then chooses not to render is
   * not protecting anything. The server refuses it independently. `canManage` is R1 and the
   * independent professions, which is exactly who bears the cost.
   */
  useEffect(() => {
    if (!canManage) return undefined;
    let cancelled = false;
    (async () => {
      setCostLoading(true);
      try {
        const { from, to } = periodRange(costPeriod);
        const res = await axios.get(
          `${API_BASE_URL}/api/service-inventory/cost-report`,
          {
            params: { from: from.toISOString(), to: to.toISOString() },
            headers: authHeaders(),
          },
        );
        if (!cancelled) setCostReport(res.data || null);
      } catch {
        // A failed report must not take the page down with it — the stock numbers above are
        // still correct and are the thing the page is for.
        if (!cancelled) setCostReport(null);
      } finally {
        if (!cancelled) setCostLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManage, costPeriod, costRefresh]);

  /**
   * The same period, and the same bar, for a caller who may not see money.
   *
   * Deliberately NOT a fallback inside the effect above: that one must keep returning early for
   * an R2, because the thing it fetches is the owner's. Two effects, two endpoints, and no path
   * on which an R2 request reaches `/cost-report`.
   */
  useEffect(() => {
    if (!canSeeUsage || canManage) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { from, to } = periodRange(costPeriod);
        const res = await axios.get(
          `${API_BASE_URL}/api/service-inventory/usage-summary`,
          {
            params: { from: from.toISOString(), to: to.toISOString() },
            headers: authHeaders(),
          },
        );
        if (!cancelled) setUsageSummary(res.data || null);
      } catch {
        // Same reasoning as the report: a missing extra bar must not take down a page whose
        // stock numbers are still correct and are the thing it is for.
        if (!cancelled) setUsageSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canSeeUsage, canManage, costPeriod]);

  /**
   * "What each service uses" USED TO BE FETCHED HERE, and it is gone on purpose.
   *
   * It asked an owner to declare in advance how much of every product every service consumes. It
   * was correct, it was optional, and it still had to be TAUGHT before it did anything — and the
   * people using this are stylists and shop owners, not cost accountants. A feature that needs a
   * course is not a feature.
   *
   * The prefill it existed to provide now comes from the ledger itself: what this service used
   * last time. See `GET /last-used`. Nothing to set up, and it cannot go stale.
   */

  const openCreate = () => {
    setEditingItem(null);
    setItemForm({
      name: "",
      unit: "ml",
      packSize: "",
      unitCost: "",
      category: "",
      supplierName: "",
      lowStockThreshold: "",
      // Answered once per PRODUCT, so a container of it is never nagged about early or left silent.
      // 30 days is the default because most consumables are. See OPEN_TOO_LONG_CHOICES.
      openTooLongDays: "30",
      notes: "",
      // Enabled on CREATE only: recording the purchase at the same time is the common
      // case, and it avoids the "why is my item 0 ml" surprise entirely.
      initialQuantity: "",
      usageMode: "per_appointment",
    });
    setShowItemForm(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name || "",
      unit: item.unit || "ml",
      packSize: item.packSize ?? "",
      unitCost: item.unitCost ?? "",
      category: item.category || "",
      supplierName: item.supplierName || "",
      lowStockThreshold: item.lowStockThreshold ?? "",
      openTooLongDays: String(item.openTooLongDays ?? 30),
      notes: item.notes || "",
      initialQuantity: "", // ADDITIVE, on create and on edit — see the field's hint
      usageMode: item.usageMode || "per_appointment",
    });
    setShowItemForm(true);
  };

  const saveItem = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const headers = authHeaders();
      if (editingItem) {
        // `unit` is not sent: the server refuses to change it, because every stored
        // quantity and every historical movement is expressed in it.
        const { unit, initialQuantity, ...editable } = itemForm;
        await axios.patch(
          `${API_BASE_URL}/api/service-inventory/items/${editingItem._id}`,
          editable,
          { headers },
        );

        /**
         * STOCK ADDED FROM THE EDIT FORM GOES THROUGH THE LEDGER, as a `receive` movement.
         *
         * Writing the balance directly would leave the ledger unable to explain the number
         * — and the ledger is the only thing that can answer "where did this go", which is
         * the entire reason it exists. Doing it here rather than making the owner close the
         * form and find the Add stock button also removes the case that produced the
         * original "why is my item 0" confusion: two screens for one purchase.
         *
         * It only ever ADDS. A number that should go DOWN is a correction, and that is a
         * different act with a different audit trail — it says so rather than silently
         * accepting whatever was typed.
         */
        const added = Number(initialQuantity);
        if (Number.isFinite(added) && added > 0) {
          await axios.post(
            `${API_BASE_URL}/api/service-inventory/receive`,
            {
              itemId: editingItem._id,
              quantity: added,
              notes: "Added while editing the item",
            },
            { headers },
          );
          setNotice(
            `Updated ${itemForm.name} and added ${formatQty(added, itemForm.unit)} ${UNIT_SUFFIX[itemForm.unit] || itemForm.unit}.`,
          );
        } else {
          setNotice(`Updated ${itemForm.name}.`);
        }
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/api/service-inventory/items`,
          itemForm,
          { headers },
        );
        setNotice(res.data?.message || `Added ${itemForm.name}.`);
      }
      setShowItemForm(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to save this item.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Archive — the answer for anything with history. Hides it everywhere, keeps the record.
   *
   * `includeInactive` is what makes this reversible from the screen, so an archived item is
   * never lost behind an API flag nobody can reach.
   */
  const toggleArchive = async (item) => {
    setBusy(true);
    setError("");
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/api/service-inventory/items/${item._id}/archive`,
        { archived: item.isActive !== false },
        { headers: authHeaders() },
      );
      setNotice(res.data?.message || "Updated.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to archive this item.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Delete — offered only when the item has no history, and the server enforces that
   * rather than trusting the screen. A refusal comes back explaining that archiving keeps
   * the record intact, which is the useful thing to say at that moment.
   */
  const deleteItem = async (item) => {
    const confirmed = window.confirm(
      `Delete ${item.name}? This only works if it has never been used.`,
    );
    if (!confirmed) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/api/service-inventory/items/${item._id}`,
        { headers: authHeaders() },
      );
      setNotice(res.data?.message || "Deleted.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to delete this item.");
    } finally {
      setBusy(false);
    }
  };

  const openAllocate = (item) => {
    setAllocateItem(item);
    // Default to every location selected: "send this to all my shops" is the common case,
    // and unticking one is faster than ticking four.
    setAllocateLocationIds(locations.map((loc) => loc._id));
    setAllocateQty("");
    setAllocateNotes("");
  };

  const submitAllocate = async (event) => {
    event.preventDefault();
    if (!allocateItem) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/service-inventory/allocate`,
        {
          itemId: allocateItem._id,
          locationIds: allocateLocationIds,
          quantityPerLocation: Number(allocateQty),
          notes: allocateNotes,
        },
        { headers: authHeaders() },
      );
      const failed = res.data?.failures?.length || 0;
      setNotice(res.data?.message || "Stock allocated.");
      if (failed) {
        setError(
          `${failed} location(s) could not be allocated — see the history for what did go through.`,
        );
      }
      setAllocateItem(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to allocate this stock.");
    } finally {
      setBusy(false);
    }
  };

  const totalsByItem = useMemo(() => {
    const map = new Map();
    for (const row of stock) {
      const key = String(row.itemId);
      map.set(key, (map.get(key) || 0) + (Number(row.quantity) || 0));
    }
    return map;
  }, [stock]);

  /*
    THE PRODUCT DETAIL VIEW IS A FILTER OVER DATA THE PAGE ALREADY HAS.

    `/stock`, `/outstanding` and `/bottles` all come back keyed by item, so narrowing them to one
    product costs no requests. Only the history is fetched, because that one is genuinely
    per-item and paginated — and it has to be fetched rather than sliced out of the page's 50 most
    recent movements, or a product last used 60 movements ago would show no history at all.

    These live up here with the other memos, above the early return, because a hook after a
    conditional return changes the hook order between renders.
  */
  const detailStock = useMemo(
    () => (detailItem ? stock.filter((row) => String(row.itemId) === String(detailItem._id)) : []),
    [stock, detailItem],
  );
  const detailOutstanding = useMemo(
    () =>
      detailItem ? outstanding.filter((row) => String(row.itemId) === String(detailItem._id)) : [],
    [outstanding, detailItem],
  );
  const detailBottles = useMemo(
    () => (detailItem ? bottles.filter((row) => String(row.itemId) === String(detailItem._id)) : []),
    [bottles, detailItem],
  );
  /** Everywhere at once: the storeroom, each shop, and each pair of hands. */
  const detailTotal = useMemo(
    () => detailStock.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
    [detailStock],
  );
  const detailWithStaff = useMemo(
    () => detailOutstanding.reduce((sum, row) => sum + (Number(row.outstanding) || 0), 0),
    [detailOutstanding],
  );
  const detailReady = useMemo(
    () => detailStock.reduce((sum, row) => sum + (Number(row.onShelf ?? row.quantity) || 0), 0),
    [detailStock],
  );

  /*
    THE STOREROOM IS NOT A SHOP, so it does not sit in the list of shops.

    It used to be one row among the locations, reading "Storeroom — not sent to a shop yet" — which
    quietly says that the place stock is KEPT and the places stock is USED are the same kind of
    thing. They are not: one is a store, the other is where the work happens, and the owner acts on
    them differently. It gets its own block, above the shops, everywhere it appeared.

    Split only for non-independent callers. An R4/R7's `locationId: null` row is their OWN shelf,
    not a storeroom, and splitting it would invent a distinction their business does not have.
  */
  const storeroomStock = useMemo(
    () => (isIndependent ? [] : stock.filter((row) => !row.locationId)),
    [stock, isIndependent],
  );
  const shopStock = useMemo(
    () => (isIndependent ? stock : stock.filter((row) => row.locationId)),
    [stock, isIndependent],
  );
  const detailStoreroom = useMemo(
    () => (isIndependent ? [] : detailStock.filter((row) => !row.locationId)),
    [detailStock, isIndependent],
  );
  const detailShops = useMemo(
    () => (isIndependent ? detailStock : detailStock.filter((row) => row.locationId)),
    [detailStock, isIndependent],
  );

  /** The same split for the cost report's "where the cost came from" table. */
  const costByLocation = costReport?.byLocation || [];
  const costStoreroom = isIndependent ? [] : costByLocation.filter((row) => !row.locationId);
  const costShops = isIndependent
    ? costByLocation
    : costByLocation.filter((row) => row.locationId);
  /** The storeroom's slice of the period's cost — the block is hidden unless there is one. */
  const storeroomCost = costStoreroom.reduce((sum, row) => sum + (Number(row.usedCost) || 0), 0);

  /**
   * A CHART MUST NOT ADD UP THINGS THAT DO NOT ADD UP.
   *
   * The obvious chart is "how much of each product do I have", and it is wrong: 200 ml plus 5
   * bottles is the same mistake the cost report already refuses to make. Two ways out, and this
   * uses both:
   *
   *   COST is money, and money translates — so the reports chart is cost per service.
   *   PER PRODUCT counts are honest — so the stock chart shows real quantities, and a product's
   *   bar is read against ITS OWN numbers, never against the bar beside it. The unit is printed in
   *   the bar's name whenever the products do not share one, because a shared axis silently invites
   *   exactly the comparison that must not be made.
   */
  const serviceChart = useMemo(() => {
    const rows = (costReport?.byService || []).filter((row) => row.cost > 0).slice(0, 8);
    return {
      labels: rows.map((row) => row.name),
      datasets: [
        {
          label: "Product cost",
          data: rows.map((row) => row.cost),
          backgroundColor: CHART_COLOURS.cost,
          borderRadius: 4,
        },
      ],
      hidden: rows.length < 2,
    };
  }, [costReport]);

  /*
    THE NUMBERS THEMSELVES, not a proportion of them.

    "80% with staff" is a figure somebody has to convert back into stock before it means anything,
    and the conversion is the part they came for. So the bars carry quantities.

    The cost of that is real and is handled rather than hidden: quantities are only comparable
    WITHIN a product. A 1000 ml bottle and a 20-count box share an axis whether you like it or not,
    so the unit is printed on the bar's name as soon as the products disagree, and the note under
    the chart says which comparison is the valid one. Only the two halves of a single bar are ever
    meant to be read against each other.
  */
  const stockChartRows = useMemo(() => {
    const byItem = new Map();
    for (const row of stock) {
      // `itemId` arrives POPULATED on a stock row, so `String(row.itemId)` is "[object Object]" and
      // matches nothing. Normalised here, because this same key is what ties the usage figures back
      // to the right product.
      const key = String(row.itemId?._id || row.itemId);
      if (!byItem.has(key)) {
        byItem.set(key, {
          itemId: key,
          name: row.name,
          unit: row.unit || null,
          ready: 0,
          withStaff: 0,
        });
      }
      const entry = byItem.get(key);
      entry.ready += Number(row.onShelf ?? row.quantity) || 0;
      entry.withStaff += Number(row.held) || 0;
    }
    return [...byItem.values()]
      .filter((row) => row.ready + row.withStaff > 0)
      .sort((a, b) => b.ready + b.withStaff - (a.ready + a.withStaff))
      .slice(0, 8);
  }, [stock]);

  const stockChart = useMemo(() => {
    // Named with the unit only when the products on screen do not share one — otherwise every bar
    // would carry the same suffix and it would read as part of the product's name.
    const units = new Set(stockChartRows.map((row) => row.unit).filter(Boolean));
    const showUnit = units.size > 1;
    /*
      WHAT WAS USED, as a third bar per product.

      Stock alone answers "how much is left" and never "is it going down", which is the question
      an owner actually has. The figure comes from the cost report, so it is for the SELECTED period
      and not for all time — the series name says which period, because a number with no period
      attached is the kind of figure people quote for a year and then argue about.

      Matched on itemId, so a product with no usage simply has no third bar rather than a zero that
      looks like "used none" when the truth is "not in this period's report".

      TWO SOURCES, ONE BAR. The owner's figures come from the cost report; a shop admin's come
      from the money-free usage summary. Both are keyed by itemId and both are quantities for
      the same period, so the bar means the same thing either way — what differs is only how
      much the viewer is allowed to know about it.
    */
    const usedByItem = new Map(
      (costReport?.byItem || usageSummary?.byItem || []).map((row) => [
        String(row.itemId),
        Number(row.usedQty) || 0,
      ]),
    );
    const hasUsage = stockChartRows.some((row) => usedByItem.has(String(row.itemId)));
    const periodLabel =
      COST_PERIODS.find((option) => option.id === costPeriod)?.label || "This period";

    return {
      labels: stockChartRows.map((row) =>
        showUnit && row.unit ? `${row.name} (${row.unit})` : row.name,
      ),
      datasets: [
        {
          label: "Ready to use",
          data: stockChartRows.map((row) => row.ready),
          backgroundColor: CHART_COLOURS.ready,
        },
        {
          label: "With staff",
          data: stockChartRows.map((row) => row.withStaff),
          backgroundColor: CHART_COLOURS.withStaff,
        },
        ...(hasUsage
          ? [
              {
                label: `Used on clients — ${periodLabel}`,
                data: stockChartRows.map((row) =>
                  usedByItem.has(String(row.itemId)) ? usedByItem.get(String(row.itemId)) : 0,
                ),
                backgroundColor: CHART_COLOURS.used,
              },
            ]
          : []),
      ],
      // Drawn from ONE product upwards. This chart is a SPLIT, not a comparison — "all of it is
      // out with staff" is worth seeing on its own. The service chart below is a comparison and
      // needs at least two things to compare.
      hidden: stockChartRows.length === 0,
      // Read by the tooltip so a figure is never printed without the unit it is counted in.
      units: showUnit ? stockChartRows.map((row) => row.unit) : [],
    };
  }, [stockChartRows, costReport, usageSummary, costPeriod]);

  const serviceChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: { label: (item) => ` ${money(item.parsed.x)} product cost` },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: { callback: (value) => `$${value}` },
          grid: { color: "#f1f5f9" },
        },
        y: { grid: { display: false } },
      },
    }),
    [],
  );

  const stockChartOptions = useMemo(
    () => ({
      /*
        ON ITS SIDE, AND STACKED — one bar per product, three segments along it.

        The product owner's call, and it is the right one: read upright and grouped, the three
        figures became three separate columns and "used" sat beside the stock figures as though it
        were a different product. Lying down, each product is ONE bar you read left to right —
        ready to use, then with staff, then what went out — which is how the question is actually
        asked ("where did this product go?").

        The trade this makes, stated so it is not discovered later: a stack implies its segments add
        up, and the grey segment is a PERIOD while the other two are a moment. So the period stays in
        its legend label, and the note under the chart says which segment it applies to. Nothing on
        this axis is money, so no total can be mistaken for a cost.
      */
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } },
        tooltip: {
          callbacks: {
            label: (item) => {
              const unit = item.chart.data.units?.[item.dataIndex];
              const value = Number(item.parsed.x) || 0;
              return ` ${item.dataset.label}: ${value}${unit && unit !== "each" ? ` ${unit}` : ""}`;
            },
          },
        },
      },
      scales: {
        x: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
        y: { stacked: true, grid: { display: false } },
      },
    }),
    [],
  );

  /*
    A PROFESSIONAL'S OWN SLICE OF THE SAME PRODUCT.

    R3 gets a DIFFERENT page, not a restricted one. `useIsCountOnly` and the section hides already
    keep the business's stock off their screen, and the detail view has to honour that: showing
    them "where it is" would hand back the shop-wide view the page deliberately withholds, and
    "with staff now" would name their colleagues' holdings. So their detail is assembled from
    `/mine` — their holding, their bottles, their movements — and nothing else.
  */
  const detailHolding = useMemo(
    () =>
      detailItem
        ? myHoldings.find((row) => String(row.itemId) === String(detailItem._id)) || null
        : null,
    [myHoldings, detailItem],
  );
  const detailMyMovements = useMemo(
    () =>
      detailItem
        ? myMovements.filter((row) => String(row.itemId) === String(detailItem._id))
        : [],
    [myMovements, detailItem],
  );

  /**
   * What is sitting in the MASTER BUCKET — `locationId: null`.
   *
   * Kept separate from the total because they answer different questions: the bucket is
   * "what have I not sent anywhere yet", the total is "how much exists at all". Showing
   * only one of them is what made an item look like it had stock when it had none.
   */
  const bucketByItem = useMemo(() => {
    const map = new Map();
    for (const row of stock) {
      if (row.locationId) continue;
      map.set(String(row.itemId), Number(row.quantity) || 0);
    }
    return map;
  }, [stock]);

  const receiveStock = async (event) => {
    event.preventDefault();
    if (!receiveItem) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/service-inventory/receive`,
        { itemId: receiveItem._id, quantity: Number(receiveQty) },
        { headers: authHeaders() },
      );

      /**
       * STRAIGHT TO THE ONLY LOCATION, when there is only one.
       *
       * A one-man shop keeps its store and its shelf in the same room, so "add stock then send it
       * to the only place it could go" is two steps for one reality. The stock is still received
       * into the bucket first and then allocated — the same two movements, in the same order — so
       * the history reads identically and nothing shortcuts the ledger. It is only the second
       * click that is removed.
       *
       * Only when there is EXACTLY ONE location: with two, "which shop?" is a real question and
       * guessing it would move somebody's stock for them.
       */
      if (receiveSendToLocation && singleLocation) {
        await axios.post(
          `${API_BASE_URL}/api/service-inventory/allocate`,
          {
            itemId: receiveItem._id,
            locationIds: [singleLocation._id],
            quantityPerLocation: Number(receiveQty),
            notes: "Sent straight from Add stock",
          },
          { headers: authHeaders() },
        );
        setNotice(
          `Added ${formatQty(Number(receiveQty), receiveItem.unit)} ${UNIT_SUFFIX[receiveItem.unit]} of ${receiveItem.name} and sent it to ${singleLocation.name}.`,
        );
      } else {
        setNotice(res.data?.message || "Stock added.");
      }

      setReceiveItem(null);
      setReceiveQty("");
      setReceiveSendToLocation(true);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to add this stock.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * BULK IMPORT HANDLERS.
   *
   * The parse runs on every keystroke rather than on submit, so the table under the box is always
   * showing what the button would actually do. An import that reports its problems AFTER it has
   * already written half the rows is the one people stop trusting.
   */
  const onImportText = (value) => {
    setImportText(value);
    setImportOutcome(null);
    setImportPreview(parseImportCsv(value));
  };

  const readImportFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      onImportText(text);
      setNotice(`Read ${file.name}. Check the preview, then import.`);
    } catch {
      setError("Could not read that file. Paste the rows into the box instead.");
    } finally {
      // Reset the input, or choosing the same file twice in a row does nothing.
      event.target.value = "";
    }
  };

  const openImport = () => {
    setImportText("");
    setImportPreview({ rows: [], fatal: null });
    setImportOutcome(null);
    setError("");
    setShowImport(true);
  };

  /**
   * The template as a real file. Building it from the same rows that fill the placeholder and the
   * "use an example" button means the thing a user downloads is the thing the screen shows.
   */
  const downloadImportTemplate = () => {
    const blob = new Blob([`${IMPORT_EXAMPLE_ROWS.join("\n")}\n`], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "service-inventory-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoked on the next tick: revoking synchronously can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const submitImport = async () => {
    const valid = importPreview.rows.filter((row) => row.errors.length === 0);
    if (valid.length === 0) return;

    setImportBusy(true);
    setError("");

    /**
     * ONE REQUEST PER PRODUCT, sequential and individually caught.
     *
     * Sequential rather than parallel: this is a small list typed once, and firing 60 simultaneous
     * creates at an endpoint that writes ledger movements is how you find out what the connection
     * pool does under load. Individually caught because a failure on one product must not abandon
     * the rest — the outcome says exactly which lines did not go in and why.
     */
    const failures = [];
    let created = 0;

    for (const row of valid) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(
          `${API_BASE_URL}/api/service-inventory/items`,
          {
            name: row.name,
            unit: row.unit,
            unitCost: row.unitCost,
            category: row.category || undefined,
            supplierName: row.supplierName || undefined,
            lowStockThreshold: row.lowStockThreshold,
            openTooLongDays: row.openTooLongDays,
            usageMode: row.usageMode,
            initialQuantity: row.initialQuantity,
          },
          { headers: authHeaders() },
        );
        created += 1;
      } catch (err) {
        failures.push({
          line: row.line,
          name: row.name,
          message: err?.response?.data?.message || "could not be added",
        });
      }
    }

    setImportOutcome({
      created,
      skipped: importPreview.rows.length - valid.length,
      failures,
    });
    setImportBusy(false);
    await load();
  };

  const openDetail = async (row) => {
    // A holding row carries `itemId` and no `_id` — it is an aggregate, not a document. Normalising
    // here means everything downstream can keep reading `detailItem._id`.
    const itemId = row._id || row.itemId;
    setDetailItem({ ...row, _id: itemId });
    setDetailHistory([]);
    setDetailLoading(true);
    setError("");
    try {
      const [history] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/service-inventory/movements?itemId=${itemId}&limit=25`, {
          headers: authHeaders(),
        }),
      ]);
      setDetailHistory(history.data?.movements || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load this product.");
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * USE ON AN APPOINTMENT — the version of "Record use" that also says WHY.
   *
   * `consume` without an appointment is a quantity with a date and no reason. That is enough to
   * keep the shelf honest, and not enough to answer the only question anybody asks about product
   * cost: what did it cost me per service? Tying each consumption to the booking that caused it
   * is what turns a period total into a per-service figure.
   *
   * The appointment list is the professional's OWN (`/api/bookings/staff`), so this cannot record
   * against somebody else's job, and the window is deliberately two-sided: a professional usually
   * records usage at the end of the day, and sometimes before the client arrives.
   */
  const openApptUse = async (row) => {
    setApptUse({ locationId: row.locationId, appointmentRefs: [], sharedServiceId: "" });
    setApptLines({});
    setApptDefaults([]);
    setError("");
    await loadApptOptions();
  };

  /**
   * Toggling an appointment on, and prefilling only when the answer is unambiguous.
   *
   * The same product is usually the same amount on every client, which is what makes batch entry
   * worth having. But a prefill can only come from ONE service's recent usage, so it is only
   * applied when every selected job shares a service. If they differ, the amounts start empty and
   * the form says why — filling them from an arbitrary one of them would present a guess as a
   * fact, which is exactly what the per-service cost would then be built on.
   */
  const toggleAppointment = async (bookingId) => {
    const selected = apptUse.appointmentRefs.includes(bookingId)
      ? apptUse.appointmentRefs.filter((id) => id !== bookingId)
      : [...apptUse.appointmentRefs, bookingId];

    const services = new Set(
      apptOptions
        .filter((booking) => selected.includes(booking._id))
        .map((booking) => String(booking.serviceId?._id || booking.serviceId || ""))
        .filter(Boolean),
    );

    const sharedServiceId = services.size === 1 ? [...services][0] : "";
    setApptUse((prev) => ({ ...prev, appointmentRefs: selected, sharedServiceId }));

    if (!sharedServiceId) {
      setApptDefaults([]);
      setApptLines({});
      return;
    }

    try {
      /*
        PRE-FILLED FROM WHAT HAPPENED LAST TIME, not from a settings screen.

        This used to read "standard amounts" the owner had to declare in advance for every product
        and every service. It was optional and it was still wrong: it is a costing model that has to
        be taught before it does anything. The ledger already knows — somebody recorded 30ml of
        Developer on a Balayage last week, and that is a better answer than a number typed into a
        form, costs nobody any setup, and cannot go stale.
      */
      const res = await axios.get(`${API_BASE_URL}/api/service-inventory/last-used`, {
        params: { serviceId: sharedServiceId },
        headers: authHeaders(),
      });
      const list = Array.isArray(res.data?.suggestions) ? res.data.suggestions : [];
      setApptDefaults(list);

      const prefilled = {};
      for (const row of list) {
        if (row.itemId) prefilled[String(row.itemId)] = String(row.quantity);
      }
      setApptLines(prefilled);
    } catch {
      setApptDefaults([]);
    }
  };

  const submitApptUse = async (event) => {
    event.preventDefault();
    if (!apptUse?.appointmentRefs?.length) return;
    setBusy(true);
    setError("");
    try {
      const lines = Object.entries(apptLines)
        .map(([itemId, quantity]) => ({ itemId, quantity: Number(quantity) }))
        .filter((line) => Number.isFinite(line.quantity) && line.quantity > 0);

      if (lines.length === 0) {
        setError("Say how much of at least one product was used on each job.");
        return;
      }

      // ONE request, one movement PER appointment: each carries its own appointmentRef, so each
      // is separately attributable. The batch only says they were entered together.
      const res = await axios.post(
        `${API_BASE_URL}/api/service-inventory/consume-batch`,
        {
          locationId: apptUse.locationId,
          appointmentRefs: apptUse.appointmentRefs,
          lines,
        },
        { headers: authHeaders() },
      );

      const failed = res.data?.failures || [];
      setNotice(res.data?.message || "Recorded against the appointments.");
      if (failed.length) {
        // Reported rather than rolled back: the ones that succeeded are true records, and
        // discarding them would lose real history to tidy up a message.
        setError(
          `${failed.length} job(s) could not be recorded — ${failed[0].message}`,
        );
      }
      setApptUse(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to record this usage.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Narrow the business's appointments to the few that could plausibly be it.
   *
   * Three weeks either side of the usage, and delivered only — the same rule `/consume` enforces,
   * repeated here because a hidden option is a kindness and the server is still the one deciding.
   */
  const openAttribute = async (usage) => {
    setAttributeTarget(usage);
    setAttributeRef("");
    setAttributeOptions([]);
    setError("");
    setAttributeLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/bookings/all`, {
        headers: authHeaders(),
      });
      const all = Array.isArray(res.data?.bookings) ? res.data.bookings : [];
      const when = new Date(usage.createdAt).getTime();
      const windowMs = 21 * 24 * 60 * 60 * 1000;
      const near = all
        .filter((booking) => !["cancelled", "no-show"].includes(booking.status))
        .filter((booking) => {
          const at = new Date(booking.appointmentDate || booking.createdAt).getTime();
          return Number.isFinite(at) && Math.abs(at - when) <= windowMs;
        })
        .sort(
          (a, b) =>
            new Date(b.appointmentDate || b.createdAt) -
            new Date(a.appointmentDate || a.createdAt),
        )
        .slice(0, 60);
      setAttributeOptions(near);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load appointments.");
    } finally {
      setAttributeLoading(false);
    }
  };

  const submitAttribute = async (event) => {
    event.preventDefault();
    if (!attributeTarget || !attributeRef) return;
    setAttributeSaving(true);
    setError("");
    try {
      await axios.post(
        `${API_BASE_URL}/api/service-inventory/usage-attributions`,
        { movementId: attributeTarget._id, appointmentRef: attributeRef },
        { headers: authHeaders() },
      );
      setNotice("Attributed — that cost is now charged to the service it belongs to.");
      setAttributeTarget(null);
      // Refetch, so the numbers on screen change. Optimistically editing them here would leave the
      // page disagreeing with the report it just changed.
      setCostRefresh((n) => n + 1);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to attribute that usage.");
    } finally {
      setAttributeSaving(false);
    }
  };

  /**
   * WHO THE APPOINTMENT WAS FOR.
   *
   * Not every booking has a linked customer ACCOUNT — a walk-in has `customerId: null` and the name
   * on the booking itself (`customerName`). Reading only `customerId` printed "Customer" on every
   * row of a demo book that plainly had names in it, which reads as missing data rather than as a
   * different kind of booking. One resolver, so a client is named wherever a client is listed.
   */
  const clientNameOf = (booking) =>
    [booking?.customerId?.firstName, booking?.customerId?.lastName].filter(Boolean).join(" ") ||
    booking?.customerName ||
    "Client";

  /** How an appointment reads in a picker: who, what, and when. */
  const appointmentLabel = (booking) => {
    const customer = clientNameOf(booking);
    const when = booking.appointmentDate
      ? new Date(booking.appointmentDate).toLocaleDateString()
      : "";
    const time = booking.startTime ? ` ${booking.startTime}` : "";
    return `${
      [customer || "Client", booking.serviceId?.name].filter(Boolean).join(" — ")
    } · ${when}${time}`;
  };

  /**
   * OPEN A CONTAINER.
   *
   * No stock moves. The container was already off the shelf the moment it was issued, so opening
   * it starts a list — that is the whole reason the cost can be divided later, when the number of
   * appointments it covered has stopped being a guess.
   */
  const startBottle = async (row) => {
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/service-inventory/bottles/start`,
        { itemId: row.itemId, locationId: row.locationId },
        { headers: authHeaders() },
      );
      setNotice(res.data?.message || "Container opened.");
      await load();
      if (res.data?.bottle) await openBottlePanel(res.data.bottle);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to open this container.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * THE ONE STOCK MOVEMENT in the whole container flow, and the moment the cost becomes known.
   *
   * Confirmed first, because it cannot be undone by a button: it is a real consumption, and the
   * correction for a mistake is the same as for any other movement — a reversing entry.
   */
  const finishBottle = async (bottle) => {
    const covered = bottle.coveredCount;
    /*
      WORDED FOR THE VIEWER, AND THE `.toFixed` IS WHY THIS IS NOT COSMETIC.

      The money fields are ABSENT — not zero — for anyone who may not see the buying price, so the
      old text would have thrown `Cannot read properties of undefined` on the person at the desk
      finishing a container on somebody else's behalf, i.e. exactly the flow this panel exists for.
    */
    const costLine = bottle.seesCost
      ? covered
        ? `Its cost of $${bottle.containerCost.toFixed(2)} will be divided across the ${covered} job${covered === 1 ? "" : "s"} it covers — $${bottle.shareCost.toFixed(2)} each.`
        : `Its cost of $${bottle.containerCost.toFixed(2)} will not be charged to any service.`
      : covered
        ? `Its cost will be divided across the ${covered} job${covered === 1 ? "" : "s"} it covers.`
        : `Its cost will not be charged to any service.`;

    const confirmed = window.confirm(
      covered
        ? `Finish this ${bottle.name}?\n\n${costLine}\n\nThis takes one container out of stock.`
        : `Finish this ${bottle.name} with no jobs on it?\n\n${costLine} You can still add jobs afterwards.`,
    );
    if (!confirmed) return;

    setBottleSaving(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/service-inventory/bottles/${bottle._id}/finish`,
        {},
        { headers: authHeaders() },
      );
      setNotice(res.data?.message || "Container finished.");
      setBottlePanel(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to finish this container.");
    } finally {
      setBottleSaving(false);
    }
  };

  const openBottlePanel = async (bottle) => {
    setBottlePanel(bottle);
    setBottleEditing(false);
    setCoveredAppointments([]);
    setError("");
    // Both at once: the tickable candidates, and the jobs already on the container — the second
    // is what makes an old container correctable, because the first is bounded to a window.
    await Promise.all([loadApptOptions(bottle.holderStaffId), loadCovered(bottle._id)]);
  };

  const loadCovered = async (bottleId) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/service-inventory/bottles/${bottleId}/appointments`,
        { headers: authHeaders() },
      );
      setCoveredAppointments(res.data?.appointments || []);
    } catch {
      setCoveredAppointments([]);
    }
  };

  /**
   * Clients on this container that the window above does not contain.
   *
   * They are listed separately rather than merged, because they are a different fact: these are
   * already decided, and the list above is still a choice. Merging them would put a ticked row
   * among unticked ones with no way to tell which was which.
   */
  const coveredOutsideWindow = useMemo(() => {
    const inWindow = new Set(apptOptions.map((booking) => String(booking._id)));
    return coveredAppointments.filter((row) => !inWindow.has(String(row.bookingId)));
  }, [coveredAppointments, apptOptions]);

  /**
   * WHO PUT THIS JOB ON, when it was not the holder — otherwise null.
   *
   * The candidate list comes from the appointment book and knows nothing about the container; the
   * container's own ticks know who added them. This looks the answer up across the two, which is why
   * it takes a booking id rather than a row.
   *
   * Only TICKED rows are asked, because only a tick is a claim. An unticked candidate is a choice not
   * yet made, and naming somebody against it would say a decision had been taken when none had.
   */
  const coveredByOther = useCallback(
    (bookingId) => {
      const row = coveredAppointments.find(
        (tick) => String(tick.bookingId) === String(bookingId),
      );
      return row?.delegated ? row.addedByName : null;
    },
    [coveredAppointments],
  );

  /**
   * MAY THE PERSON LOOKING CHANGE THIS CONTAINER'S JOBS — **asked, not worked out.**
   *
   * The server decides it (`bottleViewFor`) because the rule depends on things the browser does not
   * reliably know: the caller's ACTIVE PROFILE, and whether the container came out of a shop they are
   * posted to. The client used to keep its own copy of the rule, and two copies of a rule is how they
   * drift — the browser would have been wrong in both directions, silently.
   *
   * So the screen reads `canChangeJobs` off the container it was handed, and there is exactly one
   * place where the answer comes from. `actingOnBehalf` is the second fact the wording needs: the
   * person looking is NOT the holder, so anything they do is a delegation and says so.
   *
   * `bottleReadOnly` — WHEN it may be changed, which is a different question. A finished container is
   * a record already in reporting, so it opens locked and whoever may change it presses Update first.
   */
  const maySetContainerClients = Boolean(bottlePanel?.canChangeJobs);
  const bottleReadOnly = Boolean(
    bottlePanel &&
      (!maySetContainerClients || (!bottlePanel.isOpen && !bottleEditing)),
  );

  /**
   * Ticking an appointment IS the record — there is no save step.
   *
   * Each tick is a fact about a container, not a draft, and a draft list would need its own
   * failure handling for the case where half of it saved. So it goes straight through, and the
   * share shown next to it recalculates from the response.
   */
  const toggleBottleAppointment = async (bottle, bookingId) => {
    const already = bottle.appointments.some(
      (row) => String(row.bookingId) === String(bookingId),
    );
    setBottleSaving(true);
    setError("");
    try {
      const res = already
        ? await axios.delete(
            `${API_BASE_URL}/api/service-inventory/bottles/${bottle._id}/appointments/${bookingId}`,
            { headers: authHeaders() },
          )
        : await axios.post(
            `${API_BASE_URL}/api/service-inventory/bottles/${bottle._id}/appointments`,
            { appointmentRefs: [bookingId] },
            { headers: authHeaders() },
          );

      const updated = res.data?.bottle;
      if (updated) {
        setBottlePanel(updated);
        setBottles((rows) =>
          rows.map((row) => (row._id === updated._id ? updated : row)),
        );
        if (res.data?.failures?.length) {
          setError(res.data.failures[0].message);
        }
        // The covered list is what decides the divisor, so it is re-read rather than adjusted by
        // hand — an optimistic edit here would drift from the record the server just wrote.
        await loadCovered(updated._id);
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to update this container.");
    } finally {
      setBottleSaving(false);
    }
  };

  /**
   * THE APPOINTMENTS OF THE PERSON WHO HOLDS THE CONTAINER — not the viewer's own diary.
   *
   * This list is "tick the jobs this container served", and a container belongs to a PERSON. It
   * used to call `GET /bookings/staff`, which answers a narrower question: *the caller's* own
   * appointments, resolved from a Staff record against the caller's active role. The owner has no
   * Staff record, so that call answers 404 for them, the catch turns it into an empty list, and the
   * panel reported **"No appointments in the last 60 days"** on a container whose own subtitle said
   * it covered twenty. A list that is empty for a reason nobody can see is worse than no list.
   *
   * So: owner/admin reads the business-wide list and filters it down to the holder; anyone else
   * reads their own, because the holder IS them. Every row then names a client and a service, and
   * only those jobs — never a colleague's — can end up sharing this container's cost.
   */
  const loadApptOptions = async (holderStaffId) => {
    setApptLoading(true);
    try {
      const iso = (date) => date.toISOString().slice(0, 10);
      const from = iso(new Date(Date.now() - 60 * 24 * 60 * 60 * 1000));
      const to = iso(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
      const businessWide = (role === "R1" || role === "R2") && Boolean(holderStaffId);

      // `/bookings/all` was the obvious answer for an owner and it is the wrong one: it does not
      // populate `customerId`, so every row came back as "— Customer —". A list of anonymous jobs
      // cannot be ticked with any confidence. `/bookings/staff/:id` is the holder's own diary and
      // DOES populate the client and the service, which is the whole point of showing the list.
      const res = businessWide
        ? await axios.get(`${API_BASE_URL}/api/bookings/staff/${holderStaffId}`, {
            headers: authHeaders(),
          })
        : await axios.get(`${API_BASE_URL}/api/bookings/staff`, {
            params: { startDate: from, endDate: to },
            headers: authHeaders(),
          });

      const list = Array.isArray(res.data?.bookings) ? res.data.bookings : [];
      const day = (booking) => String(booking.appointmentDate || "").slice(0, 10);

      setApptOptions(
        list
          // `/staff/:id` takes no window server-side, so the same one is applied here. One rule,
          // whichever endpoint answered.
          .filter((booking) => {
            const when = day(booking);
            return when && when >= from && when <= to;
          })
          .filter((booking) => !["cancelled", "no-show"].includes(booking.status))
          .filter((booking) => booking.isTestBooking !== true)
          // Most recent first: a container's jobs are usually the ones just delivered.
          .sort(
            (a, b) =>
              day(b).localeCompare(day(a)) ||
              String(b.startTime || "").localeCompare(String(a.startTime || "")),
          ),
      );
    } catch {
      setApptOptions([]);
    } finally {
      setApptLoading(false);
    }
  };

  /**
   * HAND STOCK TO A NAMED PERSON.
   *
   * This is the desk's stock action, and it is the accountable one: the movement names who
   * received it, so the person named can see it on their own page and be asked about it. That
   * is why it needs no approval while an R2's count correction does — a correction changes a
   * number nobody can check, an issue changes a number AND names somebody.
   *
   * The roster is fetched when the modal opens, not with the page: only R1/R2 ever need it, and
   * a shop admin's screen should not carry a list it will never show.
   */
  const openIssue = async (item) => {
    setIssueItem(item);
    setIssueStaffId("");
    setIssueQty("");
    setIssueNotes("");
    setError("");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/service-inventory/staff`, {
        headers: authHeaders(),
      });
      setStaffOptions(res.data?.staff || []);
    } catch {
      setStaffOptions([]);
    }
  };

  const submitIssue = async (event) => {
    event.preventDefault();
    if (!issueItem) return;
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/service-inventory/issue`,
        {
          itemId: issueItem.itemId,
          locationId: issueItem.locationId,
          quantity: Math.abs(Number(issueQty)),
          issuedToStaffId: issueStaffId,
          notes: issueNotes || undefined,
        },
        { headers: authHeaders() },
      );
      setNotice(res.data?.message || "Stock issued.");
      setIssueItem(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to issue this stock.");
    } finally {
      setBusy(false);
    }
  };

  const loadingView = (
    <div className="svinv-page">
      <p className="svinv-sub">Loading service inventory…</p>
    </div>
  );
  if (loading) {
    return <PageShell role={role}>{loadingView}</PageShell>;
  }

  // Shown in the Edit form so "how many am I adding" has something to be added to. The
  // money path of a purchase is not the place to make somebody remember a number.
  const editingTotal = editingItem
    ? totalsByItem.get(String(editingItem._id)) || 0
    : 0;

  /**
   * The OPEN container for a holding, if there is one.
   *
   * Matched on item AND location, because that is exactly the uniqueness the server enforces —
   * two open containers of one product would split its cost across two lists and halve the share
   * for every appointment on both.
   */
  /**
   * The one location this business operates from, if it has exactly one.
   *
   * Used to collapse "add stock, then send it to the shop" into one action for a one-man shop,
   * where the store and the shelf are the same room. Returns null when there are none or several,
   * because then the destination is a real question rather than a formality.
   */
  const singleLocation = locations.length === 1 ? locations[0] : null;

  const openBottleFor = (row) =>
    bottles.find(
      (bottle) =>
        bottle.isOpen &&
        String(bottle.itemId) === String(row.itemId) &&
        (bottle.locationId || null) === (row.locationId || null),
    ) || null;

  /** How many rows of the paste would actually go in — the footer, the button label and the
   *  disabled state all read from this one number, so they can never contradict each other. */
  const importReady = importPreview.rows.filter((row) => row.errors.length === 0).length;

  /**
   * TWO ROUTES, ONE COMPONENT.
   *
   * Stock and Reports are separate pages with separate URLs and separate menu entries, but they
   * share this component — and deliberately share its helpers. The vocabulary, the money and
   * quantity formatters, the role rules and the page shell have been through four rounds of
   * correction; a second copy for the reports page would start drifting from this one the moment
   * either was touched, and this file's whole history is a record of what silent drift costs.
   *
   * The distinction that matters to a user is preserved: different URL, different title,
   * different sidebar entry, and NO stock tables on the reports page.
   */
  const showReports = view === "reports";
  // Derived from the URL rather than from the role: the path is what the user can see and what
  // the browser will reload to. Stripping a trailing `/reports` gives whichever base this role
  // is actually sitting on — `/dashboard/inventory`, `/dashboard/freelancer/inventory`, etc.
  const basePath = window.location.pathname.replace(/\/reports\/?$/, "").replace(/\/$/, "");

  const content = (
    <div className="svinv-page">
      <div className="svinv-head">
        <div>
          <h1 className="svinv-title">
            {isProfessional
              ? "My Service Products"
              : showReports
                ? "Inventory Reports"
                : "Service Inventory"}
          </h1>
          <p className="svinv-sub">
            {isProfessional
              ? "What you have been given, and what is left of it."
              : showReports
                ? "What product cost you, what each service normally uses, and the full history."
                : "Products you use on clients — not the products you sell."}
          </p>
        </div>
        {/* Available on BOTH views. Adding a product is not a stock-only act, and making somebody
            navigate back to the other page to add one is friction for no benefit. */}
        {canManage ? (
          <div className="svinv-head-actions">
            <button type="button" className="svinv-btn ghost" onClick={openImport}>
              Import CSV
            </button>
            <button type="button" className="svinv-btn" onClick={openCreate}>
              + Add item
            </button>
          </div>
        ) : null}
      </div>

      {error ? (
        <div role="alert" className="svinv-notice error">
          {error}
        </div>
      ) : null}
      {notice ? <div className="svinv-notice ok">{notice}</div> : null}

      {/*
        THE THREE STEPS, in the order they actually happen, shown once.
        Only on the stock view, only for someone who can act on it, and only while the inventory
        is empty — an "getting started" panel on a page with 40 products is clutter.
      */}
      {!showReports && canManage && items.length === 0 && !guideDismissed ? (
        <div className="svinv-guide">
          <div className="svinv-guide-head">
            <strong>Start here</strong>
            <button
              type="button"
              className="svinv-guide-close"
              aria-label="Dismiss this guide"
              title="Dismiss"
              onClick={() => {
                localStorage.setItem("ogestyle_inventory_guide_dismissed", "1");
                setGuideDismissed(true);
              }}
            >
              ×
            </button>
          </div>
          <p className="svinv-guide-note">
            Three steps, and you only do the first one today. This is for the products you use{" "}
            <em>on</em> clients — the ones you sell are under Products.
          </p>
          <ol className="svinv-guide-steps">
            <li>
              <strong>Add your products</strong> — the things you buy and use up. A name is enough;
              cost and unit can come later.
            </li>
            <li>
              <strong>Add stock</strong> — how much of each you have right now. Buying more later
              uses the same button.
            </li>
            <li>
              {locations.length > 1 ? (
                <>
                  <strong>Send it to a shop</strong> — pick which one. Moving stock to a shop does
                  not use it up.
                </>
              ) : (
                <>
                  <strong>It goes to your shop for you</strong> — with one location, Add stock sends
                  it straight there.
                </>
              )}
            </li>
          </ol>
          <button type="button" className="svinv-btn sm" onClick={openCreate}>
            Add your first product
          </button>
        </div>
      ) : null}

      {/* Business-level counters. Not for R3: "items", "locations holding stock" and "low
          stock" all describe the business's inventory, and none of them is a number a
          professional is responsible for. Their own figures are in the table below. */}
      {!isProfessional ? (
        <div className="svinv-tiles">
          {[
            { label: "Items", value: summary?.itemCount ?? items.length },
            {
              label: isIndependent ? "Your stock" : "Shops holding stock",
              value: summary?.locationsWithStock ?? 0,
            },
            { label: "Low stock", value: summary?.lowStock?.length ?? 0 },
            // The two numbers on this page that are asking for something get the same
            // at-a-glance treatment as the ones that are merely informative. A count is how you
            // find out there is something to do without scrolling for it.
            ...(canApprove
              ? [{ label: "Waiting for you", value: adjustments.length }]
              : []),
          ].map((tile) => (
            <div className="svinv-tile" key={tile.label}>
              <div className="svinv-tile-value">{tile.value}</div>
              <div className="svinv-tile-label">{tile.label}</div>
            </div>
          ))}
        </div>
      ) : null}

      {/*
        TABS THAT ARE LINKS. They were local state, so "the cost report" had no URL, could not be
        bookmarked, and — the reason this changed — was invisible: styled as pills, they read as a
        filter, and the page's own owner did not know the tab bar existed.

        AWAITING APPROVAL STAYS IN STOCK, deliberately. It is a stock correction waiting to happen,
        and burying something that needs a decision inside a report is how it goes unnoticed.
      */}
      {isProfessional ? null : (
        <div className="svinv-tabs">
          <Link
            to={basePath}
            className={`svinv-tabbtn${showReports ? "" : " selected"}`}
            aria-current={showReports ? undefined : "page"}
          >
            Stock
          </Link>
          <Link
            to={`${basePath}/reports`}
            className={`svinv-tabbtn${showReports ? " selected" : ""}`}
            aria-current={showReports ? "page" : undefined}
          >
            Reports
          </Link>
        </div>
      )}

      {showReports ? null : (
      <div className="svinv-tabpanel">

      {/* ---- Low stock: the alert, not just a count ---- */}
      {canManage || canIssue ? (
        summary?.lowStock?.length ? (
          <section className="svinv-section">
            <h2>
              Running low
              <span className="svinv-badge pending" style={{ marginLeft: "8px" }}>
                {summary.lowStock.length}
              </span>
              <Why>
                Measured on what is <strong>ready to use at a shop</strong>, not on what exists
                somewhere in the building. A container in somebody&apos;s hands cannot be handed to a
                client, so a shop holding two of them and no more is genuinely out.
              </Why>
            </h2>
            <div className="svinv-card">
              <table className="svinv-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Where</th>
                    <th className="num">Ready to use</th>
                    <th className="num">With staff</th>
                    <th className="num">Warn below</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.lowStock.map((row) => (
                    <tr key={`${row.itemId}-${row.locationId || "master"}`}>
                      <td>
                        <span className="svinv-item-name">{row.name || "Item"}</span>
                      </td>
                      <td>
                        {row.locationId
                          ? locationLabel({
                              name: row.locationName,
                              city: row.locationCity,
                            })
                          : "Storeroom"}
                      </td>
                      <td className="num">
                        <span className="svinv-qty">
                          {formatQty(row.onShelf, row.unit)}{" "}
                          {row.unit === "each" ? "" : UNIT_SUFFIX[row.unit]}
                        </span>
                      </td>
                      <td className="num">
                        {row.held > 0 ? formatQty(row.held, row.unit) : "—"}
                      </td>
                      <td className="num">{formatQty(row.threshold, row.unit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null
      ) : null}

      {/* ---- Awaiting approval (R1 only) ---- */}
      {canApprove && adjustments.length > 0 ? (
        <section className="svinv-section">
          <h2>
            Waiting for your approval
            <span className="svinv-badge pending" style={{ marginLeft: "8px" }}>
              {adjustments.length}
            </span>
          </h2>
          {/* Kept visible, not put behind the ": this is the one section that is asking the
              reader for something. */}
          <p className="svinv-section-note">
            Nothing here has changed any stock yet. Approving applies it; rejecting leaves the
            shelf exactly as it is.
          </p>
          <div className="svinv-card">
            <table className="svinv-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Location</th>
                  <th className="num">Change</th>
                  <th>Asked by</th>
                  <th>Reason</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {adjustments.map((adjustment) => (
                  <tr key={adjustment._id}>
                    <td>{adjustment.itemId?.name || "—"}</td>
                    <td>
                      {isIndependent
                        ? "You"
                        : locationLabel(adjustment.locationId)}
                    </td>
                    <td className="num">
                      <span className="svinv-badge neg">
                        {Number(adjustment.quantity) > 0 ? "+" : ""}
                        {formatQty(adjustment.quantity, adjustment.itemId?.unit)}
                      </span>
                    </td>
                    <td>
                      {[adjustment.recordedByUserId?.firstName,
                        adjustment.recordedByUserId?.lastName]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </td>
                    <td>{adjustment.reason || "—"}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="svinv-btn ghost sm"
                        style={{ marginRight: "8px" }}
                        disabled={busy}
                        onClick={() =>
                          decideAdjustment(adjustment._id, false).catch(() => {})
                        }
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        className="svinv-btn sm"
                        disabled={busy}
                        onClick={() =>
                          decideAdjustment(adjustment._id, true).catch(() => {})
                        }
                      >
                        Approve
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/*
        THE PICTURE, AFTER THE THINGS THAT NEED DOING.

        It sat above the alerts, which pushed "running low" and "waiting for approval" below the
        fold on a laptop — the two things on this page that are actually asking for something.
        A chart is something you look at once you know there is nothing urgent; it is not a
        substitute for being told there is.

        REAL QUANTITIES. It used to show percentages, on the reasoning that ml and whole units cannot
        share an axis — true, but it cost the reader a conversion, and the conversion was the thing
        they opened the page for. The axis is shared and the UNITS are printed instead, with one line
        saying which comparison is the valid one (the two halves of a bar). See `stockChartRows`.
      */}
      {!isProfessional && !stockChart.hidden ? (
        <div className="svinv-chart">
          <div className="svinv-chart-title">
            {stockChart.datasets.length > 2 ? "Stock and use" : "Ready to use, or out with staff"}
          </div>
          <div className="svinv-chart-canvas">
            <Bar data={stockChart} options={stockChartOptions} />
          </div>
          {/*
            Two sentences, for the two ways this chart can be misread.

            The shared axis: a product measured in ml sits beside one measured in whole units, so
            the only comparison that holds is within a single bar.

            The stack: segments imply they add up, and the grey one is a PERIOD while the other two
            are a moment. Saying which is which is the whole of the fix — a reader who knows that
            cannot be misled by the shape.
          */}
          {stockChart.units.length || stockChart.datasets.length > 2 ? (
            <p className="svinv-chart-note">
              {stockChart.units.length
                ? "Each product is counted in its own unit, so read along one bar — not one product against another."
                : null}
              {stockChart.units.length && stockChart.datasets.length > 2 ? " " : null}
              {stockChart.datasets.length > 2
                ? "The grey part is what was used over the period named in the legend; the first two parts are what you have right now."
                : null}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* ---- Items ----

          HIDDEN FROM R3. A professional's page is "what am I responsible for", and a
          catalogue of the business's buying prices is neither theirs to see nor anything they
          can act on. Their numbers live in "Given to you" below. */}
      {!isProfessional ? (
      <section className="svinv-section">
        <h2>
          {canAllocate ? "Products" : canManage ? "Your products" : "Products at this shop"}
          <Why>
            {canAllocate
              ? "What you bought, in the unit it is measured in. Sending stock to a shop does not use it up — it moves it to where it will be used."
              : canManage
                ? "The stock you own and manage. The shop&apos;s stock is never mixed in."
                : "Only what is held at this shop. The business&apos;s central stock and buying prices are not shown."}
          </Why>
        </h2>
        {canManage ? (
          <div className="svinv-picker-tools" style={{ marginBottom: "10px" }}>
            <button type="button" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
          </div>
        ) : null}
        {items.length === 0 ? (
          <p className="svinv-empty">
            {canManage ? (
              <>
                Nothing here yet. Press <strong>+ Add item</strong> above for the first product you
                buy — its name is enough to start, and you can fill in the cost later. Once it
                exists, <strong>Add stock</strong> on its row records what you have.
              </>
            ) : (
              "Nothing has been sent to this shop yet. It appears here as soon as the owner sends stock."
            )}
          </p>
        ) : (
          <div className="svinv-card">
            <table className="svinv-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Unit</th>
                  {/* Cost is the owner's buying information, and this page is shared. */}
                  {canManage ? <th>Unit cost</th> : null}
                  <th>
                    {canAllocate
                      ? "In the storeroom"
                      : canManage
                        ? "In stock"
                        : "At this shop"}
                  </th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const bucket = bucketByItem.get(String(item._id)) || 0;
                  const total = totalsByItem.get(String(item._id)) || 0;
                  /**
                   * THE NUMBER THE HEADING CLAIMS, which is not the same number for
                   * everybody.
                   *
                   * `bucketByItem` holds only `locationId: null` rows — the master bucket. A
                   * shop admin's `/stock` deliberately excludes it, so reading this column
                   * from the bucket gave an R2 a confident "0 at your location" for stock
                   * sitting on their own shelf, with the real 15 printed further down the same
                   * screen. Two answers to one question, and the wrong one first.
                   *
                   * So: the owner gets the bucket (stock not sent anywhere yet), everyone
                   * else gets the sum of the rows they can actually see — their location, or
                   * their own stock.
                   */
                  const shown = canAllocate ? bucket : total;
                  const archived = item.isActive === false;
                  return (
                    <tr
                      key={item._id}
                      className="svinv-row-link"
                      style={archived ? { opacity: 0.55 } : undefined}
                      onClick={() => openDetail(item)}
                    >
                      <td>
                        <button
                          type="button"
                          className="svinv-name-link"
                          onClick={(event) => {
                            // The row is clickable too; without this the event would bubble and
                            // open the same product twice.
                            event.stopPropagation();
                            openDetail(item);
                          }}
                        >
                          {item.name}
                        </button>
                        {archived ? (
                          <span className="svinv-badge pending">archived</span>
                        ) : null}
                        {item.category ? (
                          <span className="svinv-item-meta">{item.category}</span>
                        ) : null}
                      </td>
                      <td>
                        {UNIT_SUFFIX[item.unit] || item.unit}
                        {item.packSize ? (
                          <span className="svinv-pack">
                            {formatQty(item.packSize, item.unit)}{" "}
                            {UNIT_SUFFIX[item.unit]} per pack
                          </span>
                        ) : null}
                        {/* Stated on the row because it decides what the professional is asked
                            for, and R1 is the one who set it. */}
                        {item.usageMode === "shared" ? (
                          <span className="svinv-pack">Product in use</span>
                        ) : null}
                      </td>
                      {canManage ? <td>{money(item.unitCost)}</td> : null}
                      <td>
                        <span className="svinv-qty">
                          {formatQty(shown, item.unit)}{" "}
                          {item.unit === "each" ? "" : UNIT_SUFFIX[item.unit]}
                        </span>
                        {/* The distinction that was missing: what is still yours to send
                            anywhere, versus how much exists across the business. */}
                        {canAllocate ? (
                          <span className="svinv-pack">
                            {bucket > 0
                              ? `${formatQty(total, item.unit)} in total`
                              : "not sent anywhere yet"}
                          </span>
                        ) : null}
                      </td>
                      {/*
                        ONE ACTION ON THE ROW, AND EVERYTHING ELSE ONE CLICK IN.

                        This cell held up to five buttons, and the list was unreadable because of
                        it. Add stock stays because buying something is the most common thing an
                        owner does here, and burying it would put a click in front of the single
                        most frequent task. Send to shop, Correct the count, Edit, Archive and
                        Delete moved into the product's own page, where there is room to say what
                        each one will do.
                      */}
                      <td className="actions">
                        {canManage && !archived ? (
                          <button
                            type="button"
                            className="svinv-btn sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setReceiveItem(item);
                              setReceiveQty("");
                              // Default back to the shortcut each time, so a previous "leave it in
                              // my own stock" does not silently become the answer for the next
                              // purchase — on a one-location business the common case is the
                              // opposite.
                              setReceiveSendToLocation(true);
                            }}
                          >
                            Add stock
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      ) : null}

      {/* ---- Given to me (the accountability view) ---- */}
      {isProfessional ? (
        <section className="svinv-section">
          <h2>Products given to you</h2>

          {myHoldings.length === 0 ? (
            /* NOT an error, and NOT hidden. A blank page here is indistinguishable from a
               broken one, so it says which of the two it is. */
            <p className="svinv-empty">
              Nothing has been handed to you yet. When the desk issues product for you to use,
              it appears here with the amount you still have.
            </p>
          ) : (
            <div className="svinv-card">
              <table className="svinv-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Received</th>
                    <th className="num">Used</th>
                    <th className="num">Returned</th>
                    <th className="num">Left with you</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {myHoldings.map((row) => (
                    <tr key={row.itemId}>
                      <td>
                        {/* The product name is the way in. NOT the whole row: this row has action
                            buttons, and a mis-click that opens a dialog while somebody is trying
                            to record usage is worse than an extra pixel of target. */}
                        <button
                          type="button"
                          className="svinv-name-link"
                          onClick={() => openDetail(row)}
                        >
                          {row.name}
                        </button>
                        {row.locationName ? (
                          <span className="svinv-item-meta">{row.locationName}</span>
                        ) : null}
                      </td>
                      <td className="num">
                        {formatQty(row.received, row.unit)}{" "}
                        {row.unit === "each" ? "" : UNIT_SUFFIX[row.unit]}
                      </td>
                      <td className="num">
                        {formatQty(row.used, row.unit)}{" "}
                        {row.unit === "each" ? "" : UNIT_SUFFIX[row.unit]}
                      </td>
                      <td className="num">
                        {formatQty(row.returned, row.unit)}{" "}
                        {row.unit === "each" ? "" : UNIT_SUFFIX[row.unit]}
                      </td>
                      <td className="num">
                        <span className="svinv-qty">
                          {formatQty(row.outstanding, row.unit)}{" "}
                          {row.unit === "each" ? "" : UNIT_SUFFIX[row.unit]}
                        </span>
                      </td>
                      <td className="actions">
                        {/*
                          ONLY WHEN THE SYSTEM KNOWS WHICH SHELF. Consumption reduces a
                          location's balance, so without one the server refuses it. Hiding the
                          button and saying why is better than a button that fails — the
                          professional cannot supply this, they were never told it.
                        */}
                        {row.locationId ? (
                          <>
                            {/*
                              TWO ACTIONS, NOT FIVE.

                              What the professional is asked for depends on ONE thing the owner
                              already decided: is this product used once per appointment, or does
                              one container serve many?

                                per_appointment -> "Use on a job"   (which job used it)
                                shared          -> "Start a bottle" (which jobs it covers)

                              "Used one" and a separate "Hand back" are gone. A bare "used 1"
                              with no appointment is exactly the cost-with-no-owner this whole
                              model exists to eliminate, and handing back was already a choice
                              inside the action modal.
                            */}
                            {row.usageMode === "shared" ? (
                              openBottleFor(row) ? (
                                <button
                                  type="button"
                                  className="svinv-btn sm"
                                  style={{ marginRight: "8px" }}
                                  disabled={busy}
                                  onClick={() => openBottlePanel(openBottleFor(row))}
                                  title="Add or remove the jobs this container covers, then finish it when it is empty"
                                >
                                  Container: {openBottleFor(row).coveredCount} job
                                  {openBottleFor(row).coveredCount === 1 ? "" : "s"} — manage
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="svinv-btn sm"
                                  style={{ marginRight: "8px" }}
                                  disabled={busy}
                                  onClick={() => startBottle(row)}
                                  title="One container, many jobs. Open it, tick the jobs it covers, finish it when it is empty — the cost is divided then."
                                >
                                  Start using it
                                </button>
                              )
                            ) : (
                              <button
                                type="button"
                                className="svinv-btn sm"
                                style={{ marginRight: "8px" }}
                                disabled={busy}
                                onClick={() => openApptUse(row)}
                                title="Records WHICH appointments used it, so product cost can be worked out per service"
                              >
                                Use on a job
                              </button>
                            )}
                            <button
                              type="button"
                              className="svinv-btn ghost sm"
                              disabled={busy}
                              onClick={() => {
                                setUseItem({
                                  itemId: row.itemId,
                                  name: row.name,
                                  unit: row.unit,
                                  packSize: row.packSize,
                                  quantity: row.outstanding,
                                  locationId: row.locationId,
                                  fromHolding: true,
                                });
                                setUseQty("");
                                setUseReason("");
                                setUseMode("consume");
                              }}
                            >
                              More options
                            </button>
                          </>
                        ) : (
                          <span className="svinv-pack">
                            No location recorded — ask the desk to re-issue this to you
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* The evidence behind the numbers above. A total with no visible history is
              just a claim; seeing each movement is what makes it checkable. */}
          {myMovements.length > 0 ? (
            <>
              <h3
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#6b7280",
                  margin: "18px 0 8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.03em",
                }}
              >
                Recent activity
              </h3>
              <div className="svinv-card">
                <table className="svinv-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>What</th>
                      <th>Item</th>
                      <th className="num">Quantity</th>
                      <th>Where</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myMovements.map((movement) => (
                      <tr key={movement._id}>
                        <td>{new Date(movement.createdAt).toLocaleString()}</td>
                        <td>{MOVEMENT_LABELS[movement.type] || movement.type}</td>
                        <td>{movement.itemName || "—"}</td>
                        <td className="num">
                          {formatQty(Math.abs(movement.quantity), movement.unit)}{" "}
                          {movement.unit === "each" ? "" : UNIT_SUFFIX[movement.unit]}
                        </td>
                        <td>
                          {locationLabel({
                            name: movement.locationName,
                            city: movement.locationCity,
                            state: movement.locationState,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      {/* ---- Where the stock is ----

          ALSO HIDDEN FROM R3. It is correct that a professional can see what is on their own
          shelf, but it is not what they opened this page for, and it sits directly beneath a
          table of the same item. Two answers to "how much is left", one of them about the whole
          shelf rather than about what they are holding, is how the wrong number gets quoted. */}
      {!isProfessional ? (
      <section className="svinv-section">
        <h2>
          {isIndependent ? "Your stock" : "What's in each shop"}
          <Why>
            {isIndependent
              ? "Your own products. The shop's stock is never mixed in."
              : "What is sitting at each shop. An item's total is across every shop you have."}
          </Why>
        </h2>
        {stock.length === 0 ? (
          <p className="svinv-empty">
            No stock anywhere yet — so there is nothing to record usage against.
            {canManage
              ? " Add stock first (Add stock on an item above), then this table becomes the place you record what a client used."
              : ""}
            {canAllocate ? " Use Send to shop above to move stock to a shop." : ""}
          </p>
        ) : (
          <>
          {/* The storeroom, told apart from the shops. R1 only — it is the one role that owns it. */}
          {storeroomStock.length ? (
            <div className="svinv-storeroom">
              <div className="svinv-storeroom-title">
                Storeroom <span>Stock you have bought but not sent to a shop</span>
              </div>
              <table className="svinv-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Ready to use</th>
                    {canAdjust ? <th className="actions" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {storeroomStock.map((row, index) => (
                    <tr key={`${row.itemId}-store-${index}`}>
                      <td>
                        <span className="svinv-item-name">{row.name}</span>
                      </td>
                      <td className="num">
                        <span className="svinv-qty">
                          {formatQty(row.onShelf ?? row.quantity, row.unit)}{" "}
                          {row.unit === "each" ? "" : UNIT_SUFFIX[row.unit]}
                        </span>
                      </td>
                      {canAdjust ? (
                        <td className="actions">
                          <button
                            type="button"
                            className="svinv-btn ghost sm"
                            onClick={() => {
                              setUseItem(row);
                              setUseQty("");
                              setUseReason("");
                              setUseMode("adjust");
                            }}
                          >
                            Correct the count
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <h4 className="svinv-sub-h">
            {isIndependent ? "Your stock" : "In each shop"}
          </h4>
          {shopStock.length === 0 ? (
            <p className="svinv-empty">
              Nothing has been sent to a shop yet. Use <strong>Send to shop</strong> on an item
              above — moving stock to a shop is not the same as using it.
            </p>
          ) : (
          <div className="svinv-card">
            <table className="svinv-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>{isIndependent ? "Held by" : "Location"}</th>
                  <th className="num">Quantity</th>
                  {canAdjust ? <th className="actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {shopStock.map((row, index) => {
                  const packs = formatPacks(row.quantity, row.unit, row.packSize);
                  return (
                    <tr key={`${row.itemId}-${row.locationId || "self"}-${index}`}>
                      <td>
                        <span className="svinv-item-name">{row.name}</span>
                      </td>
                      <td>
                        {isIndependent
                          ? "You"
                          : locationLabel({
                              name: row.locationName,
                              city: row.locationCity,
                              state: row.locationState,
                            })}
                      </td>
                      <td className="num">
                        {/*
                          ON THE SHELF, not the raw balance.

                          The balance is stock IN THE BUILDING, because handing a container to a
                          professional does not remove it from the business. So printing it here
                          would overstate what can actually be picked up — and that is the one
                          thing this table is for.
                        */}
                        <span className="svinv-qty">
                          {formatQty(row.onShelf ?? row.quantity, row.unit)}{" "}
                          {row.unit === "each" ? "" : UNIT_SUFFIX[row.unit]}
                        </span>
                        {row.held > 0 ? (
                          <span className="svinv-pack">
                            +{formatQty(row.held, row.unit)}{" "}
                            {row.unit === "each" ? "" : UNIT_SUFFIX[row.unit]} with staff
                          </span>
                        ) : packs ? (
                          <span className="svinv-pack">{packs}</span>
                        ) : null}
                      </td>
                      {canAdjust || canIssue ? (
                        <td className="actions">
                          {/* THE DESK'S ACTION, and it is first for R2 because it is the only
                              one that leaves a checkable record: the movement names the person
                              taking the stock, and they can see it on their own page. */}
                          {canIssue ? (
                            <button
                              type="button"
                              className="svinv-btn sm"
                              style={{ marginRight: "8px" }}
                              disabled={busy}
                              onClick={() =>
                                openIssue({
                                  itemId: row.itemId,
                                  name: row.name,
                                  unit: row.unit,
                                  packSize: row.packSize,
                                  locationId: row.locationId,
                                  // What can actually be handed over, not the balance: some of
                                  // the balance may already be out with somebody.
                                  quantity: row.onShelf ?? row.quantity,
                                })
                              }
                            >
                              Give to a stylist
                            </button>
                          ) : null}
                          {canAdjust ? (
                            <button
                              type="button"
                              className="svinv-btn ghost sm"
                              onClick={() => {
                                setUseItem(row);
                                setUseQty("");
                                setUseReason("");
                                // Business-wide stock is a store, not a shelf, and an R2
                                // records no usage at all — so for both, the count is the only
                                // thing that can be wrong. An independent's own stock IS the
                                // shelf they work from, so theirs records real usage.
                                setUseMode(
                                  (row.locationId === null && !isIndependent) ||
                                    !canRecordUsage
                                    ? "adjust"
                                    : "consume",
                                );
                              }}
                            >
                              {(row.locationId === null && !isIndependent) ||
                              !canRecordUsage
                                ? "Correct the count"
                                : "Used on a client"}
                            </button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          </>
        )}
      </section>
      ) : null}

      {/* ---- Handed out, not yet used ---- */}
      {canIssue ? (
        <section className="svinv-section">
          <h2>
            With stylists now
            <Why>
              Giving stock to a stylist takes it off the shop&apos;s shelf straight away, but it is
              still in the building — in somebody&apos;s hands — until they record that a client used
              it. So the shelf figure is only half the answer:{" "}
              <strong>what is physically in the shop = the shelf + this list</strong>. The gap closes
              when the stylist records their use, which is what makes it worth looking at.
            </Why>
          </h2>

          {outstanding.length === 0 ? (
            <p className="svinv-empty">
              Nothing is currently held by a professional — every issue has been used or
              returned.
            </p>
          ) : (
            <>
              <div className="svinv-tiles">
                {[
                  {
                    label: "With stylists",
                    value: (outstandingTotals?.holdings ?? outstanding.length).toString(),
                    hint: "Across all items",
                  },
                  {
                    label: "Stylists holding stock",
                    value: (outstandingTotals?.people ?? 0).toString(),
                    hint: "Named when given stock",
                  },
                  {
                    label: "Not yet used",
                    value: formatQty(
                      outstandingTotals?.quantity ?? 0,
                      outstanding[0]?.unit || "each",
                    ),
                    hint: "Mixed units — see the table",
                  },
                ].map((tile) => (
                  <div className="svinv-tile" key={tile.label}>
                    <div className="svinv-tile-value">{tile.value}</div>
                    <div className="svinv-tile-label">
                      {tile.label}
                      {tile.hint ? (
                        <span className="svinv-tile-hint">{tile.hint}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              <div className="svinv-card">
                <table className="svinv-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Held by</th>
                      <th>At</th>
                      <th className="num">Issued</th>
                      <th className="num">Used</th>
                      <th className="num">Left unused</th>
                      {canManage ? <th className="num">Value</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {outstanding.map((row) => (
                      <tr key={`${row.itemId}-${row.locationId || "none"}-${row.staffId}`}>
                        <td>
                          <span className="svinv-item-name">{row.name}</span>
                        </td>
                        <td>{row.staffName}</td>
                        <td>
                          {row.locationId
                            ? locationLabel(row)
                            : "No location recorded"}
                        </td>
                        <td className="num">
                          {formatQty(row.issued, row.unit)}
                          {row.unit === "each" ? "" : ` ${UNIT_SUFFIX[row.unit] || ""}`}
                        </td>
                        <td className="num">
                          {formatQty(row.used, row.unit)}
                          {row.unit === "each" ? "" : ` ${UNIT_SUFFIX[row.unit] || ""}`}
                        </td>
                        <td className="num">
                          <span className="svinv-qty">
                            {formatQty(row.outstanding, row.unit)}{" "}
                            {row.unit === "each" ? "" : UNIT_SUFFIX[row.unit]}
                          </span>
                        </td>
                        {canManage ? <td className="num">{money(row.value)}</td> : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      ) : null}

      {/* ---- Shared containers ---- */}
      {canSeeContainers && bottles.length > 0 ? (
        <section className="svinv-section">
          <h2>
            Products in use
            <Why>
              An opened container — a bottle, a can, a tube — that is still being used up across
              jobs. Its cost is divided across the jobs it covers{" "}
              <strong>when it is finished</strong> — nothing is charged while it is open, because
              nobody knows yet how many jobs it will do.
              <br />
              <br />
              <strong>See the jobs</strong> opens the list this container&apos;s cost is divided
              across — and it is the only place that list can be changed, so a container finished
              with the wrong jobs on it can still be put right afterwards.
              <br />
              <br />
              These figures are about <strong>containers</strong>. Usage recorded without naming
              the job is a different list, and it lives on <strong>Inventory Reports</strong>.
            </Why>
          </h2>

          <div className="svinv-tiles">
            {[
              {
                label: "Open",
                value: (bottleSummary?.open ?? 0).toString(),
                hint: "In use right now",
              },
              /*
                FIRST, because it is the only tile here that is asking for something.

                Nothing is divided until a container is FINISHED — that is the design, and it is
                right. Its cost is that a container nobody remembers to finish divides nothing at
                all: the money sits on no service and every screen looks correct the whole time.
                The other tiles are facts about money already accounted for; this one is money that
                is not, yet.
              */
              ...(bottleSummary?.stale
                ? [
                    {
                      label: "Open too long",
                      value: String(bottleSummary.stale),
                      hint: "Nothing divided yet — open longer than this product should last",
                    },
                  ]
                : []),
              {
                // "CONTAINER" IS IN THE LABEL, because this counted every container while the cost
                // report counted the period, and — worse — sat a few inches away from a DIFFERENT
                // "no client attached" figure that is about usage entries rather than containers.
                // Two similar numbers with similar names is how somebody attributes some usage,
                // watches the wrong number, and concludes the app is broken.
                label: "Container cost on a service",
                value: money(bottleSummary?.allocated),
                hint: "Every container, all time",
              },
              {
                label: "Containers with nobody ticked",
                value: money(bottleSummary?.unallocated),
                hint: "Finished, no jobs on them",
              },
            ].map((tile) => (
              <div
                className={`svinv-tile${/too long/.test(tile.label) ? " warn" : ""}`}
                key={tile.label}
              >
                <div className="svinv-tile-value">{tile.value}</div>
                <div className="svinv-tile-label">
                  {tile.label}
                  {tile.hint ? <span className="svinv-tile-hint">{tile.hint}</span> : null}
                </div>
              </div>
            ))}
          </div>

          <div className="svinv-card">
            <table className="svinv-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Opened</th>
                  <th className="num">Covers</th>
                  {bottles[0]?.seesCost ? <th className="num">Each carries</th> : null}
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {bottles.slice(0, 25).map((bottle) => (
                  <tr key={bottle._id}>
                    <td>
                      <span className="svinv-item-name">{bottle.name}</span>
                      {!bottle.isOpen ? (
                        <span className="svinv-badge pending">finished</span>
                      ) : null}
                      {/*
                        ONLY PAST THE THRESHOLD. An age on every open container would be a number
                        nobody asked for, and a warning that is always on is not a warning.
                      */}
                      {bottle.openTooLong ? (
                        <span className="svinv-age">open {bottle.openDays} days</span>
                      ) : null}
                      {bottle.seesCost && bottle.unallocatedCost ? (
                        <span className="svinv-item-meta">
                          finished with nobody ticked — {money(bottle.unallocatedCost)} not charged to a service
                        </span>
                      ) : null}
                    </td>
                    <td>{new Date(bottle.startedAt).toLocaleDateString()}</td>
                    <td className="num">{bottle.coveredCount}</td>
                    {bottle.seesCost ? (
                      <td className="num">
                        {bottle.coveredCount ? money(bottle.shareCost) : "—"}
                      </td>
                    ) : null}
                    <td className="actions">
                      <button
                        type="button"
                        className="svinv-btn ghost sm"
                        onClick={() => openBottlePanel(bottle)}
                      >
                        {
                          // "View" said nothing about what you would see. The panel is the list of
                          // JOBS this container's cost is split across, and it is the only place
                          // that split can be changed.
                          //
                          // JOBS, NOT CLIENTS. The ticks are the DIVISOR, and a client can appear
                          // on two of them — one person who came twice and used the same container.
                          // "Add clients" would then say one while the number dividing the money
                          // said two, so the label contradicted the arithmetic. A job is the unit
                          // being counted; the client and the service are shown ON it.
                          //
                          // AND THE LABEL FOLLOWS THE ABILITY. "Add jobs" is an instruction, and
                          // the desk cannot carry it out — the server refuses them. Offering an
                          // action that is then refused is worse than offering none. The desk gets
                          // "See the jobs" either way; only the holder is invited to add.
                          maySetContainerClients && bottle.isOpen
                            ? "Add jobs"
                            : "See the jobs"
                        }
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      </div>
      )}

      {showReports ? (
      <>

      {/*
        BUYING PRICES ARE THE OWNER'S. A shop admin reaching this page is told so plainly rather
        than shown an empty space — the server refuses `/cost-report` for them anyway, so the
        absence is a rule and not a failure, and it should read like one.
      */}
      {!canManage ? (
        <section className="svinv-section">
          <h2>What product cost you</h2>
          <p className="svinv-empty">
            Buying prices are the owner&apos;s, so this report is not shown to you. What is at your
            shop, who is holding it, and what has gone out of it this period are all on{" "}
            <strong>Stock</strong> — the chart there shows use without showing spend.
          </p>
        </section>
      ) : null}

      {/* ---- What product cost ---- */}
      {canManage ? (
        <section className="svinv-section">
          <h2>
            What product cost you
            <Why>
              What each item cost <em>when it was used</em>, so a price rise today does not change
              last month&apos;s figures. Stock that was only moved between shops is not counted —
              moving a bottle does not spend it.{" "}
              <strong>Cost per job</strong> is this period&apos;s product cost divided by the jobs it
              was used on: an average, never a number anybody types in. It is only as good as the
              attribution — product with no client recorded against it is shown separately rather
              than quietly averaged in.
            </Why>
          </h2>

          <div className="svinv-periods">
            {COST_PERIODS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`svinv-period${costPeriod === option.id ? " selected" : ""}`}
                aria-pressed={costPeriod === option.id}
                onClick={() => setCostPeriod(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {costLoading ? (
            <p className="svinv-empty">Adding it up…</p>
          ) : !costReport ? (
            <p className="svinv-empty">
              The cost report could not be loaded. Your stock numbers above are unaffected.
            </p>
          ) : (
            <>
              <div className="svinv-tiles">
                {[
                  {
                    label: "Product cost",
                    value: money(costReport.totals?.productCost),
                    hint: "Used on clients",
                  },
                  {
                    label: "Lost",
                    value: money(costReport.totals?.lostCost),
                    hint: "Earned nothing",
                  },
                  {
                    label: "Received",
                    value: money(costReport.totals?.receivedCost),
                    hint: "What you bought",
                  },
                  {
                    label: "Corrections",
                    value: money(costReport.totals?.correctedCost),
                    hint: "Count changes",
                  },
                ].map((tile) => (
                  <div className="svinv-tile" key={tile.label}>
                    <div className="svinv-tile-value">{tile.value}</div>
                    <div className="svinv-tile-label">
                      {tile.label}
                      {tile.hint ? (
                        <span className="svinv-tile-hint">{tile.hint}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              {/*
                WHY THERE IS NOTHING IN THE PERIOD — stated, not left to be inferred.
                "$0.00" on its own is ambiguous in a way that matters: it reads as "product
                is free", when it can also mean "nobody recorded any usage". Those have
                opposite consequences and only one of them is fine.
              */}
              {costReport.totals?.movements === 0 ? (
                <p className="svinv-empty">
                  Nothing was recorded at all in this period — no purchases, usage or
                  corrections.
                </p>
              ) : (costReport.totals?.usedQty || 0) === 0 ? (
                <p className="svinv-empty">
                  No product was recorded as <strong>used on a client</strong> in this
                  period. {costReport.totals?.movements} other movement
                  {costReport.totals?.movements === 1 ? "" : "s"} happened. If clients were
                  served, use <strong>Used on a client</strong> on the shop that did the work —
                  otherwise this figure stays at zero however much product leaves the shelf.
                </p>
              ) : null}

              {/*
                EVERY TABLE HAS A HEADING AND AN EMPTY STATE.

                These three tables used to vanish when they had no rows. A vanished table is
                indistinguishable from a broken one — the reviewer sees the totals, no per-service
                figure, and has no way to tell whether the report failed or the business genuinely
                charged nothing to a service this month. A table that says WHY it is empty is the
                difference between a report and a shrug.
              */}
              <h4 className="svinv-sub-h">Used, product by product</h4>
              {costReport.byItem?.length ? (
                <div className="svinv-card">
                  <table className="svinv-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="num">Used</th>
                        <th className="num">Cost</th>
                        <th className="num">Lost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costReport.byItem.map((row) => (
                        // Clicking a product opens that product's own page. The report was the end
                        // of the road before: it said a product cost money and gave the reader
                        // nowhere to go with it.
                        <tr
                          key={row.itemId}
                          className="svinv-row-link"
                          onClick={() =>
                            openDetail(
                              // The report's row is an aggregate, so it carries no unit cost or
                              // usage mode. The item itself fills those in; without it the detail
                              // would open missing its own subtitle.
                              items.find((item) => String(item._id) === String(row.itemId)) || row,
                            )
                          }
                        >
                          <td>
                            <button
                              type="button"
                              className="svinv-name-link"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDetail(
                                  items.find(
                                    (item) => String(item._id) === String(row.itemId),
                                  ) || row,
                                );
                              }}
                            >
                              {row.name}
                            </button>
                            {row.category ? (
                              <span className="svinv-item-meta">{row.category}</span>
                            ) : null}
                          </td>
                          <td className="num">
                            {formatQty(row.usedQty, row.unit)}
                            {row.unit === "each" ? "" : ` ${UNIT_SUFFIX[row.unit] || ""}`}
                          </td>
                          <td className="num">{money(row.usedCost)}</td>
                          <td className="num">{money(row.lostCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="svinv-empty">
                  No product was used in this period. The totals above are what was bought, lost
                  and corrected — nothing was consumed on a client.
                </p>
              )}

              <h4 className="svinv-sub-h">Where the cost came from</h4>
              {costByLocation.length ? (
                <>
                {/*
                  Cost taken straight out of the storeroom — used up before it was ever sent to a
                  shop. Its own block, because it is a different place doing a different job.

                  Shown only when there IS such cost. The endpoint returns a storeroom row whether
                  or not anything was used out of it, and rendering that row unconditionally put an
                  empty table on the page reading "COST / —", which looks like a broken report
                  rather than a quiet month."
                */}
                {storeroomCost > 0 ? (
                  <div className="svinv-storeroom">
                    <div className="svinv-storeroom-title">
                      Storeroom <span>Used before it was sent to a shop</span>
                    </div>
                    <div className="svinv-storeroom-figure">{money(storeroomCost)}</div>
                  </div>
                ) : null}
                <h4 className="svinv-sub-h">
                  {isIndependent ? "From your own stock" : "In each shop"}
                </h4>
                {costShops.length === 0 ? (
                  <p className="svinv-empty">
                    No product was used in a shop this period.
                  </p>
                ) : (
                <div className="svinv-card">
                  {/*
                    COST ONLY, no quantity column.

                    A location holds several items in different units, so a "total used"
                    here would add 200 ml to 5 bottles and print a number that means
                    nothing. Money is the one thing that translates, so money is what is
                    shown — the per-item table above carries the quantities.
                  */}
                  <table className="svinv-table">
                    <thead>
                      <tr>
                        <th>Shop</th>
                        <th className="num">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costShops.map((row) => (
                        <tr key={row.locationId}>
                          <td>{locationLabel(row)}</td>
                          <td className="num">{money(row.usedCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
                </>
              ) : (
                <p className="svinv-empty">
                  No cost came from anywhere in this period — nothing was used at a shop or out of
                  the storeroom.
                </p>
              )}

              {/* WHAT EACH SERVICE COST — the figure everything else was built for. */}
              <h4 className="svinv-sub-h">
                What each service cost
                <Why>
                  <strong>Every product the service used, added together.</strong> A service is not
                  one product — it is however many its jobs consumed, plus its share of any
                  container that served several clients. &quot;Used directly&quot; is what the
                  appointments for that service recorded; &quot;Containers&quot; is the shares of
                  multi-client containers charged to it. Click a service to see which products made
                  up its figure.
                </Why>
              </h4>
              {!serviceChart.hidden ? (
                <div className="svinv-chart">
                  <div className="svinv-chart-title">Product cost by service</div>
                  <div className="svinv-chart-canvas tall">
                    <Bar data={serviceChart} options={serviceChartOptions} />
                  </div>
                </div>
              ) : null}
              {costReport.byService?.length ? (
                <div className="svinv-card">
                  <table className="svinv-table">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th className="num">Clients</th>
                        <th className="num">Used directly</th>
                        <th className="num">Containers</th>
                        <th className="num">Product cost</th>
                        <th className="num">Per client</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costReport.byService.map((row) => (
                        <tr
                          key={row.serviceId || "unknown"}
                          className="svinv-row-link"
                          onClick={() => setServiceDetail(row)}
                        >
                          <td>
                            <button
                              type="button"
                              className="svinv-name-link"
                              onClick={(event) => {
                                event.stopPropagation();
                                setServiceDetail(row);
                              }}
                            >
                              {row.name}
                            </button>
                          </td>
                          <td className="num">{row.jobs}</td>
                          <td className="num">{money(row.directCost)}</td>
                          {/* A container's cost divided across the jobs it covered. Shown apart
                              from direct usage because they are worked out differently — one is
                              what was used on the job, the other is this job's share of a
                              container that served several. */}
                          <td className="num">{money(row.bottleCost)}</td>
                          <td className="num">
                            <strong>{money(row.cost)}</strong>
                          </td>
                          <td className="num">
                            {row.jobs ? money(row.cost / row.jobs) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="svinv-empty">
                  {costReport.totals?.usedCost ? (
                    <>
                      No product is charged to a service in this period, because none of the usage
                      names the client it was used on. The cost is shown below as not charged to
                      any service rather than spread across them.{" "}
                      {costReport.unattributed?.howToFix}
                    </>
                  ) : (
                    <>No product was used in this period, so there is nothing to charge to a
                      service.</>
                  )}
                </p>
              )}

              {/*
                THE UNATTRIBUTED REMAINDER, stated rather than spread around.

                Folding it into the services above would make every one of them look slightly more
                expensive and make the total impossible to check. It is a real cost with no service
                behind it, and saying so is more useful than a smoother number.
              */}
              {costReport.totals?.unattributedCost ? (
                <div className="span-2" style={{ marginTop: "10px" }}>
                  <div className="svinv-choice-note">
                    <strong>
                      {money(costReport.totals.unattributedCost)} is not charged to any service.
                    </strong>{" "}
                    {costReport.unattributed?.reason} It is left out of the per-service figures
                    above rather than averaged into them — a cost with no job behind it would make
                    every service look more expensive and the total impossible to verify.
                    <br />
                    <br />
                    {costReport.unattributed?.howToFix}
                  </div>

                  {/*
                    THE PART THAT CAN BE FIXED, shown as something you can act on.
                    The paragraph above explains the figure; this lists the entries behind it and
                    gives each one somewhere to go. A cost the reader can see and cannot act on is a
                    complaint, and it was the thing that prompted this whole feature.
                  */}
                  {costReport.unattributedMovements?.length ? (
                    <>
                      <h4 className="svinv-sub-h">
                        Usage that named no client
                        <Why>
                          These are the recorded uses with no appointment behind them, so nothing
                          can say which service caused the cost. Point one at the client it was
                          really for and its cost moves onto that service immediately. It does not
                          change the ledger — the usage is attributed, not rewritten — so the
                          record still shows when the link was made.
                        </Why>
                      </h4>
                      <div className="svinv-card">
                        <table className="svinv-table">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>When</th>
                              <th className="num">Amount</th>
                              <th className="num">Cost</th>
                              <th>Recorded by</th>
                              <th className="actions" />
                            </tr>
                          </thead>
                          <tbody>
                            {costReport.unattributedMovements.map((usage) => (
                              <tr key={usage._id}>
                                <td>
                                  <span className="svinv-item-name">{usage.name}</span>
                                  {usage.locationName ? (
                                    <span className="svinv-item-meta">{usage.locationName}</span>
                                  ) : null}
                                </td>
                                <td>{new Date(usage.createdAt).toLocaleDateString()}</td>
                                <td className="num">
                                  {formatQty(usage.quantity, usage.unit)}
                                  {usage.unit === "each"
                                    ? ""
                                    : ` ${UNIT_SUFFIX[usage.unit] || ""}`}
                                </td>
                                <td className="num">{money(usage.cost)}</td>
                                <td>{usage.recordedBy || "—"}</td>
                                <td className="actions">
                                  <button
                                    type="button"
                                    className="svinv-btn ghost sm"
                                    onClick={() => openAttribute(usage)}
                                  >
                                    Attach to a client
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {costReport.unattributed?.itemisedNote ? (
                        <p className="svinv-detail-note">
                          {costReport.unattributed.itemisedNote}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <p className="svinv-detail-note">
                      None of the remaining cost is a plain usage entry, so there is nothing to
                      attach here — {costReport.unattributed?.itemisedNote}
                    </p>
                  )}
                </div>
              ) : null}
            </>
          )}

        </section>
      ) : null}

      {/* ---- History ----

          R1/R2/R4/R7 only. This is the BUSINESS's ledger — it shows allocations between
          locations, purchases and corrections, none of which a professional took part in. */}
      {!isProfessional ? (
      <section className="svinv-section">
        <h2>
          What happened
          <Why>
            Nothing here can be edited or deleted. A mistake is corrected with a new entry that
            undoes it, so the record of what was believed at the time survives.
          </Why>
        </h2>
        {movements.length === 0 ? (
          <p className="svinv-empty">
            Nothing has happened yet. Every purchase, move, use and correction appears here, and
            none of it can be edited or deleted afterwards.
          </p>
        ) : (
          <div className="svinv-card">
            <table className="svinv-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th>Item</th>
                  <th className="num">Quantity</th>
                  <th>{isIndependent ? "Held by" : "Location"}</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr key={movement._id}>
                    <td>{new Date(movement.createdAt).toLocaleString()}</td>
                    <td>
                      {MOVEMENT_LABELS[movement.type] || movement.type}
                      {movement.approvalStatus === "pending" ? (
                        <span className="svinv-badge pending">awaiting approval</span>
                      ) : null}
                    </td>
                    <td>{movement.itemId?.name || "—"}</td>
                    <td className="num">
                      <span
                        className={
                          Number(movement.quantity) > 0
                            ? "svinv-badge pos"
                            : "svinv-badge neg"
                        }
                      >
                        {Number(movement.quantity) > 0 ? "+" : ""}
                        {formatQty(movement.quantity, movement.unit)}
                        {movement.unit === "each" ? "" : ` ${UNIT_SUFFIX[movement.unit]}`}
                      </span>
                    </td>
                    <td>
                      {isIndependent ? "You" : locationLabel(movement.locationId)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      ) : null}
      </>
      ) : null}

      {/*
        THE EXPLAINER THAT USED TO SIT HERE IS GONE, on purpose.

        It described three actions in a paragraph because the labels were model words —
        "allocate", "consume", "master inventory" — and nobody could tell them apart from the
        button text alone. The buttons now say Add stock, Send to shop and Used on a client, and
        that distinction is visible in the labels themselves. A paragraph restating what three
        well-named buttons already say is not clarity, it is noise.
      */}

      {/* ---- Attach an unattributed usage to a client ---- */}
      {attributeTarget ? (
        <Modal
          title={`Which client was ${attributeTarget.name} used on?`}
          subtitle={`${formatQty(attributeTarget.quantity, attributeTarget.unit)}${
            attributeTarget.unit === "each" ? "" : ` ${UNIT_SUFFIX[attributeTarget.unit] || ""}`
          } recorded on ${new Date(attributeTarget.createdAt).toLocaleDateString()}, worth ${money(
            attributeTarget.cost,
          )}.`}
          onClose={() => setAttributeTarget(null)}
          onSubmit={submitAttribute}
          footer={
            <ModalActions
              busy={attributeSaving}
              disabled={!attributeRef}
              onCancel={() => setAttributeTarget(null)}
              confirmLabel="Attach to this client"
            />
          }
        >
          <Field
            label="Appointment"
            required
            span2
            hint="Delivered appointments within three weeks of the usage. Cancelled and no-show jobs are left out — no service was delivered, so nothing was used on them."
          >
            {attributeLoading ? (
              <span className="hint">Loading appointments…</span>
            ) : attributeOptions.length === 0 ? (
              <span className="hint warn">
                No appointments in that window. The usage stays unattributed, which is accurate —
                it just cannot be charged to a service.
              </span>
            ) : (
              <select
                value={attributeRef}
                onChange={(e) => setAttributeRef(e.target.value)}
                required
                autoFocus
              >
                <option value="">Choose the client…</option>
                {attributeOptions.map((booking) => (
                  <option key={booking._id} value={booking._id}>
                    {appointmentLabel(booking)}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <div className="span-2">
            <div className="svinv-choice-note">
              <strong>This does not change the stock.</strong> The product was already taken off
              the shelf when it was used; only the attribution is missing. Pointing it at a client
              moves its <strong>{money(attributeTarget.cost)}</strong> out of &quot;not charged to
              any service&quot; and onto that service&apos;s cost — and the ledger keeps the
              original entry, with the date the link was made beside it.
            </div>
          </div>
        </Modal>
      ) : null}

      {/* ---- One service's cost, opened up ---- */}
      {serviceDetail ? (
        <Modal
          title={serviceDetail.name}
          subtitle={`${money(serviceDetail.cost)} of product across ${serviceDetail.jobs} client${
            serviceDetail.jobs === 1 ? "" : "s"
          }${serviceDetail.jobs ? ` — ${money(serviceDetail.cost / serviceDetail.jobs)} each` : ""}.`}
          wide
          onClose={() => setServiceDetail(null)}
          onSubmit={(event) => event.preventDefault()}
          footer={
            <button
              type="button"
              className="svinv-btn ghost sm"
              onClick={() => setServiceDetail(null)}
            >
              Close
            </button>
          }
        >
          <div className="svinv-detail">
            <div className="svinv-tiles">
              {[
                {
                  label: "Product cost",
                  value: money(serviceDetail.cost),
                  hint: "Every product this service used",
                },
                {
                  label: "Per client",
                  value: serviceDetail.jobs
                    ? money(serviceDetail.cost / serviceDetail.jobs)
                    : "—",
                  hint: `${serviceDetail.jobs} client${serviceDetail.jobs === 1 ? "" : "s"}`,
                },
                {
                  label: "Products involved",
                  value: (serviceDetail.products || []).length,
                  hint: "Listed below",
                },
              ].map((tile) => (
                <div className="svinv-tile" key={tile.label}>
                  <div className="svinv-tile-value">{tile.value}</div>
                  <div className="svinv-tile-label">
                    {tile.label}
                    <span className="svinv-tile-hint">{tile.hint}</span>
                  </div>
                </div>
              ))}
            </div>

            <h4 className="svinv-sub-h">Which products made up that figure</h4>
            {(serviceDetail.products || []).length === 0 ? (
              <p className="svinv-empty">
                No product is recorded against this service in the period.
              </p>
            ) : (
              <div className="svinv-card">
                <table className="svinv-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="num">Used on the job</th>
                      <th className="num">Share of a container</th>
                      <th className="num">Cost</th>
                      <th className="num">Share of the service</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(serviceDetail.products || []).map((product) => (
                      <tr key={product.itemId || product.name}>
                        <td>
                          <button
                            type="button"
                            className="svinv-name-link"
                            onClick={() => {
                              const item = items.find(
                                (entry) => String(entry._id) === String(product.itemId),
                              );
                              setServiceDetail(null);
                              if (item) openDetail(item);
                            }}
                          >
                            {product.name}
                          </button>
                        </td>
                        {/* Kept apart because they are worked out differently: one is what the job
                            consumed, the other is this job's share of something that served
                            several clients. */}
                        <td className="num">
                          {product.directCost
                            ? `${formatQty(product.directQty, product.unit || "each")} · ${money(
                                product.directCost,
                              )}`
                            : "—"}
                        </td>
                        <td className="num">
                          {product.containerCost
                            ? `${product.containerQty} × container · ${money(
                                product.containerCost,
                              )}`
                            : "—"}
                        </td>
                        <td className="num">
                          <strong>{money(product.cost)}</strong>
                        </td>
                        <td className="num">
                          {serviceDetail.cost
                            ? `${Math.round((product.cost / serviceDetail.cost) * 100)}%`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/*
              The parts must add up to the whole, said out loud. A breakdown that merely looks
              plausible is worse than none, because it reads as an explanation.

              Only the sources that actually contributed are named. `money(0)` renders an em dash,
              so listing both unconditionally produced "— used directly and $3.00 from containers",
              which is worse than saying nothing.
            */}
            <p className="svinv-detail-note">
              These add up to the {money(serviceDetail.cost)} at the top
              {serviceDetail.directCost > 0
                ? ` — ${money(serviceDetail.directCost)} used directly on the job`
                : ""}
              {serviceDetail.bottleCost > 0
                ? `${serviceDetail.directCost > 0 ? " and" : " —"} ${money(
                    serviceDetail.bottleCost,
                  )} from containers across several clients`
                : ""}
              .
            </p>
          </div>
        </Modal>
      ) : null}

      {/* ---- One product, in full ---- */}
      {detailItem ? (
        <Modal
          title={detailItem.name}
          subtitle={[
            detailItem.category,
            // No buying price for anyone who does not manage the inventory. `money(undefined)`
            // renders an em dash, so this has to be dropped, not left to fall through.
            canManage
              ? `${money(detailItem.unitCost)} per ${UNIT_SUFFIX[detailItem.unit] || detailItem.unit}`
              : null,
            detailItem.usageMode === "shared" ? "Product in use" : "Once per appointment",
          ]
            .filter(Boolean)
            .join(" · ")}
          wide
          onClose={() => setDetailItem(null)}
          onSubmit={(event) => event.preventDefault()}
          footer={
            // READ-ONLY FOR A PROFESSIONAL. Their actions already sit on the row they came from,
            // two inches away. Rebuilding "use on a client" and the container flow in here would
            // be a second copy of the same logic, and the two copies would drift.
            isProfessional ? (
              <button
                type="button"
                className="svinv-btn ghost sm"
                onClick={() => setDetailItem(null)}
              >
                Close
              </button>
            ) : (
              <>
              {canManage ? (
                <button
                  type="button"
                  className="svinv-btn sm"
                  onClick={() => {
                    const item = detailItem;
                    setDetailItem(null);
                    setReceiveItem(item);
                    setReceiveQty("");
                    setReceiveSendToLocation(true);
                  }}
                >
                  Add stock
                </button>
              ) : null}
              {canAllocate ? (
                <button
                  type="button"
                  className="svinv-btn info sm"
                  onClick={() => {
                    const item = detailItem;
                    setDetailItem(null);
                    openAllocate(item);
                  }}
                >
                  Send to shop
                </button>
              ) : null}
              {/*
                THE ONLY COUNT THAT CAN BE WRONG HERE.
                A shop admin never records usage, and the storeroom is a store rather than a
                shelf, so for both of them the count is the only thing that can be wrong. An
                independent's own stock IS the shelf they work from, so theirs records real usage.
                The row chosen follows from that: the storeroom if there is one, otherwise the
                shop's own row.
              */}
              {canAdjust && detailStock.length > 0 ? (
                <button
                  type="button"
                  className="svinv-btn ghost sm"
                  onClick={() => {
                    const row =
                      detailStock.find((entry) => entry.locationId === null) || detailStock[0];
                    setDetailItem(null);
                    setUseItem(row);
                    setUseQty("");
                    setUseReason("");
                    setUseMode(isIndependent ? "consume" : "adjust");
                  }}
                >
                  {isIndependent ? "Used on a client" : "Correct the count"}
                </button>
              ) : null}
              {/* Edit, Archive and Delete are in the BODY now, under "Manage", not here. A footer
                  of seven buttons gives `Delete` the same weight and the same target size as `Add
                  stock`, and on a phone it became a stack taller than the content it acts on. */}
              <button
                type="button"
                className="svinv-btn ghost sm"
                onClick={() => setDetailItem(null)}
              >
                Close
              </button>
              </>
            )
          }
        >
          <div className="svinv-detail">
            {error ? (
              <div role="alert" className="svinv-notice error">
                {error}
              </div>
            ) : null}

            {isProfessional ? (
              <>
                <div className="svinv-tiles">
                  {[
                    {
                      label: "Left with you",
                      value: `${formatQty(detailHolding?.outstanding ?? 0, detailItem.unit)} ${
                        detailItem.unit === "each" ? "" : UNIT_SUFFIX[detailItem.unit]
                      }`,
                      hint: "What you are responsible for",
                    },
                    {
                      label: "Used so far",
                      value: `${formatQty(detailHolding?.used ?? 0, detailItem.unit)} ${
                        detailItem.unit === "each" ? "" : UNIT_SUFFIX[detailItem.unit]
                      }`,
                      hint: "Recorded against clients",
                    },
                    {
                      label: "Handed back",
                      value: `${formatQty(detailHolding?.returned ?? 0, detailItem.unit)} ${
                        detailItem.unit === "each" ? "" : UNIT_SUFFIX[detailItem.unit]
                      }`,
                      hint: "Returned to the shop",
                    },
                  ].map((tile) => (
                    <div className="svinv-tile" key={tile.label}>
                      <div className="svinv-tile-value">{tile.value}</div>
                      <div className="svinv-tile-label">
                        {tile.label}
                        <span className="svinv-tile-hint">{tile.hint}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <h4 className="svinv-sub-h">Where it came from</h4>
                <p className="svinv-empty">
                  {/*
                    NOT `locationLabel(detailHolding)`. That helper reads `.name`, and a holding
                    row's `.name` is the PRODUCT — so it printed "Given to you from Detox" while
                    the movements below said "Mo's Beauty World". A holding carries its shop as
                    `locationName`, so it has to be reshaped before it can be labelled.
                  */}
                  {detailHolding?.locationName
                    ? `Given to you from ${locationLabel({
                        name: detailHolding.locationName,
                        city: detailHolding.locationCity,
                        state: detailHolding.locationState,
                      })}.`
                    : "No shop was recorded when this was given to you."}{" "}
                  You are holding{" "}
                  <strong>
                    {formatQty(detailHolding?.outstanding ?? 0, detailItem.unit)}{" "}
                    {detailItem.unit === "each" ? "" : UNIT_SUFFIX[detailItem.unit]}
                  </strong>
                  , of {formatQty(detailHolding?.received ?? 0, detailItem.unit)} handed over.
                </p>
              </>
            ) : (
              <>

            <div className="svinv-tiles">
              {[
                {
                  label: "Ready to use",
                  value: `${formatQty(detailReady, detailItem.unit)} ${
                    detailItem.unit === "each" ? "" : UNIT_SUFFIX[detailItem.unit]
                  }`,
                  hint: "On a shelf or in the storeroom",
                },
                {
                  label: "With staff",
                  value: `${formatQty(detailWithStaff, detailItem.unit)} ${
                    detailItem.unit === "each" ? "" : UNIT_SUFFIX[detailItem.unit]
                  }`,
                  hint: detailOutstanding.length
                    ? `${detailOutstanding.length} holding it`
                    : "Nobody",
                },
                {
                  label: canAllocate
                    ? "Total in the business"
                    : isIndependent
                      ? "Your total"
                      : "Total at this shop",
                  value: `${formatQty(detailTotal, detailItem.unit)} ${
                    detailItem.unit === "each" ? "" : UNIT_SUFFIX[detailItem.unit]
                  }`,
                  // Named for what the viewer can actually see. An R2 never sees the storeroom,
                  // so calling their shop's total "the business" would overclaim by exactly the
                  // amount they are not allowed to look at.
                  hint: canAllocate ? "Ready plus with staff" : "Everything you can see",
                },
                ...(canManage
                  ? [
                      {
                        label: "Value in stock",
                        value: money(detailTotal * (Number(detailItem.unitCost) || 0)),
                        hint: "Total × unit cost",
                      },
                    ]
                  : []),
              ].map((tile) => (
                <div className="svinv-tile" key={tile.label}>
                  <div className="svinv-tile-value">{tile.value}</div>
                  <div className="svinv-tile-label">
                    {tile.label}
                    <span className="svinv-tile-hint">{tile.hint}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* WHERE IT IS */}
            <h4 className="svinv-sub-h">Where it is</h4>
            {detailStock.length === 0 ? (
              <p className="svinv-empty">
                No stock anywhere yet. Use <strong>Add stock</strong> below to record what you
                bought.
              </p>
            ) : (
              <>
              {/* The storeroom first and apart, for the same reason as on the stock page: it is a
                  store, not a place where work happens. */}
              {detailStoreroom.length ? (
                <div className="svinv-storeroom">
                  <div className="svinv-storeroom-title">
                    Storeroom <span>Bought but not sent to a shop</span>
                  </div>
                  <table className="svinv-table">
                    <thead>
                      <tr>
                        <th className="num">Ready to use</th>
                        <th className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailStoreroom.map((row, index) => (
                        <tr key={`store-${index}`}>
                          <td className="num">{formatQty(row.onShelf ?? row.quantity, row.unit)}</td>
                          <td className="num">
                            <span className="svinv-qty">{formatQty(row.quantity, row.unit)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              <h4 className="svinv-sub-h">
                {isIndependent ? "With you" : "In each shop"}
              </h4>
              {detailShops.length === 0 ? (
                <p className="svinv-empty">
                  Nothing has been sent to a shop yet.
                </p>
              ) : (
              <div className="svinv-card">
                <table className="svinv-table">
                  <thead>
                    <tr>
                      <th>Shop</th>
                      <th className="num">Ready to use</th>
                      <th className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailShops.map((row, index) => (
                      <tr key={`${row.locationId || "you"}-${index}`}>
                        <td>
                          {row.locationId ? (
                            locationLabel(row)
                          ) : (
                            <span className="svinv-item-name">With you</span>
                          )}
                        </td>
                        <td className="num">
                          {formatQty(row.onShelf ?? row.quantity, row.unit)}
                        </td>
                        <td className="num">
                          <span className="svinv-qty">{formatQty(row.quantity, row.unit)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              </>
            )}

            {/* WHO HAS IT */}
            {detailOutstanding.length > 0 ? (
              <>
                <h4 className="svinv-sub-h">With staff now</h4>
                <div className="svinv-card">
                  <table className="svinv-table">
                    <thead>
                      <tr>
                        <th>Who</th>
                        <th>Where</th>
                        <th className="num">Taken</th>
                        <th className="num">Used</th>
                        <th className="num">Left</th>
                        {canManage ? <th className="num">Value</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {detailOutstanding.map((row) => (
                        <tr key={`${row.staffId}-${row.locationId || "none"}`}>
                          <td>{row.staffName}</td>
                          <td>{row.locationId ? locationLabel(row) : "No shop recorded"}</td>
                          <td className="num">{formatQty(row.issued, row.unit)}</td>
                          <td className="num">{formatQty(row.used, row.unit)}</td>
                          <td className="num">
                            <span className="svinv-qty">{formatQty(row.outstanding, row.unit)}</span>
                          </td>
                          {canManage ? <td className="num">{money(row.value)}</td> : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

            {/* PRODUCTS IN USE */}
            {detailBottles.length > 0 ? (
              <>
                <h4 className="svinv-sub-h">Products in use</h4>
                <div className="svinv-card">
                  <table className="svinv-table">
                    <thead>
                      <tr>
                        <th>Where</th>
                        <th>Opened</th>
                        <th className="num">Covers</th>
                        {detailBottles[0]?.seesCost ? (
                          <th className="num">Each carries</th>
                        ) : null}
                        <th className="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {detailBottles.map((bottle) => (
                        <tr key={bottle._id}>
                          <td>
                            {bottle.locationId ? locationLabel(bottle) : "No shop recorded"}
                            {!bottle.isOpen ? (
                              <span className="svinv-badge pending">finished</span>
                            ) : null}
                            {bottle.openTooLong ? (
                              <span className="svinv-age">open {bottle.openDays} days</span>
                            ) : null}
                          </td>
                          <td>{new Date(bottle.startedAt).toLocaleDateString()}</td>
                          <td className="num">{bottle.coveredCount}</td>
                          {bottle.seesCost ? (
                            <td className="num">
                              {bottle.coveredCount ? money(bottle.shareCost) : "—"}
                            </td>
                          ) : null}
                          <td className="actions">
                            <button
                              type="button"
                              className="svinv-btn ghost sm"
                              onClick={() => {
                                setDetailItem(null);
                                openBottlePanel(bottle);
                              }}
                            >
                              {maySetContainerClients && bottle.isOpen
                                ? "Add jobs"
                                : "See the jobs"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

              </>
            )}

            {/*
              THIS PRODUCT'S OWN HISTORY, and the source differs by role. An owner or a shop
              admin gets the ledger for that product (`/movements?itemId=`, which applies their
              own scope). A professional gets their OWN movements from `/mine` — what was handed
              to them, what they used, what they put back — because the shop's ledger for the
              same product includes colleagues' usage and is not their record to read.
            */}
            <h4 className="svinv-sub-h">What has happened</h4>
            {detailLoading ? (
              <p className="svinv-empty">Loading…</p>
            ) : (isProfessional ? detailMyMovements : detailHistory).length === 0 ? (
              <p className="svinv-empty">
                {isProfessional
                  ? "Nothing has been handed to you or recorded against this product yet."
                  : "Nothing has happened to this product yet."}
              </p>
            ) : (
              <div className="svinv-card">
                <table className="svinv-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>What</th>
                      <th className="num">Amount</th>
                      <th>Where</th>
                      {/* A professional's record is about their own work, so the last column is
                          the client. Everyone else's is about custody, so it is the person. */}
                      <th>{isProfessional ? "Client" : "Who"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(isProfessional ? detailMyMovements : detailHistory).map((movement) => (
                      <tr key={movement._id}>
                        <td>{new Date(movement.createdAt).toLocaleDateString()}</td>
                        <td>{MOVEMENT_LABELS[movement.type] || movement.type}</td>
                        <td className="num">
                          {movement.type === "adjustment" && movement.quantity < 0 ? "-" : ""}
                          {formatQty(Math.abs(movement.quantity), detailItem.unit)}
                          {detailItem.unit === "each" ? "" : ` ${UNIT_SUFFIX[detailItem.unit]}`}
                        </td>
                        <td>
                          {movement.locationId
                            ? locationLabel(movement.locationId)
                            : movement.locationName
                              ? movement.locationName
                              : isIndependent
                                ? "Your stock"
                                : "Storeroom"}
                        </td>
                        <td>
                          {isProfessional
                            ? movement.appointment?.serviceName || "—"
                            : [
                                movement.issuedToStaffId?.firstName,
                                movement.issuedToStaffId?.lastName,
                              ]
                                .filter(Boolean)
                                .join(" ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!isProfessional && detailHistory.length >= 25 ? (
              <p className="svinv-detail-note">Showing the 25 most recent entries.</p>
            ) : null}

            {/* The rare and destructive actions, set apart and below the operational ones. */}
            {canManage && !isProfessional ? (
              <div className="svinv-manage">
                <span className="svinv-manage-label">Manage</span>
                <button
                  type="button"
                  className="svinv-btn ghost sm"
                  onClick={() => {
                    const item = detailItem;
                    setDetailItem(null);
                    openEdit(item);
                  }}
                >
                  Edit details
                </button>
                <button
                  type="button"
                  className="svinv-btn ghost sm"
                  disabled={busy}
                  onClick={() => {
                    const item = detailItem;
                    setDetailItem(null);
                    toggleArchive(item);
                  }}
                >
                  {detailItem.isActive === false ? "Restore" : "Archive"}
                </button>
                <button
                  type="button"
                  className="svinv-btn ghost sm"
                  disabled={busy}
                  onClick={() => {
                    const item = detailItem;
                    setDetailItem(null);
                    deleteItem(item);
                  }}
                >
                  Delete
                </button>
              </div>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {/* ---- Add / edit item ---- */}
      {showItemForm ? (
        <Modal
          title={editingItem ? `Edit ${editingItem.name}` : "Add an item"}
          subtitle="What you buy, in the unit you measure it in. Allocation comes next."
          onClose={() => setShowItemForm(false)}
          onSubmit={saveItem}
          footer={
            <ModalActions
              busy={busy}
              onCancel={() => setShowItemForm(false)}
              confirmLabel={editingItem ? "Save changes" : "Add item"}
            />
          }
        >
          <Field label="Name" required span2>
            <input
              value={itemForm.name}
              onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
              placeholder="e.g. Developer 20 vol"
              required
              autoFocus
            />
          </Field>

          <Field
            label="Stock unit"
            required
            hint={
              editingItem
                ? "Locked — every quantity and movement already recorded is expressed in it."
                : "Everything is counted in this. A 1L bottle tracked in ml is entered as 1000."
            }
          >
            <select
              value={itemForm.unit}
              onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
              disabled={Boolean(editingItem)}
            >
              {UNITS.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </Field>

          {itemForm.unit !== "each" ? (
            <Field
              label="Pack size"
              hint={`Display only — lets the screen say “1.2 bottles left”.`}
            >
              <div className="svinv-input-group">
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={itemForm.packSize}
                  onChange={(e) =>
                    setItemForm({ ...itemForm, packSize: e.target.value })
                  }
                  placeholder="1000"
                />
                <span className="suffix">
                  {UNIT_SUFFIX[itemForm.unit]} per bottle/box
                </span>
              </div>
            </Field>
          ) : null}

          {/*
            A SEPARATE QUESTION FROM THE UNIT. `unit` says how the product is measured;
            this says whether ONE container spans several appointments. So "spray, each,
            shared" and "developer, ml, once per appointment" are both valid.
          */}
          <Field
            label="How is it used?"
            required
            span2
            hint="Decides what the professional is asked to record. A shared product is opened, ticked against the appointments it covers, then finished — and its cost is divided across those appointments at the end, when the count is a fact rather than a guess."
          >
            <div className="svinv-modes">
              {[
                {
                  id: "per_appointment",
                  label: "Once per appointment",
                  hint: "An ampoule, a foil, a measured pour — the amount per client is known.",
                },
                {
                  id: "shared",
                  label: "Shared across appointments",
                  hint: "A spray or bulk liquid — one container serves many clients.",
                },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`svinv-mode${itemForm.usageMode === option.id ? " selected" : ""}`}
                  aria-pressed={itemForm.usageMode === option.id}
                  onClick={() => setItemForm({ ...itemForm, usageMode: option.id })}
                >
                  <strong>{option.label}</strong>
                  <span>{option.hint}</span>
                </button>
              ))}
            </div>
          </Field>

          {/*
            ONLY FOR A SHARED PRODUCT, because only a shared product has containers. Asking it of a
            per-appointment item would be a question with no consequence attached to it.

            Asked in the owner's own terms — "about a month", "no reminder" — rather than in days.
            The number is an implementation detail; the judgement is the product's shelf life, and
            the person making it is the one who buys the stock.
          */}
          {itemForm.usageMode === "shared" ? (
            <Field
              label="How long should one last?"
              span2
              hint="Its cost is only divided when a container is finished, so one that is never finished charges nothing to anybody. This is how long to leave it before the screen says something — a tub of powder and a bottle of developer do not keep the same time."
            >
              <div className="svinv-modes">
                {OPEN_TOO_LONG_CHOICES.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`svinv-mode${
                      String(itemForm.openTooLongDays) === option.id ? " selected" : ""
                    }`}
                    aria-pressed={String(itemForm.openTooLongDays) === option.id}
                    onClick={() =>
                      setItemForm({ ...itemForm, openTooLongDays: option.id })
                    }
                  >
                    <strong>{option.label}</strong>
                    <span>{option.hint}</span>
                  </button>
                ))}
              </div>
            </Field>
          ) : null}

          <Field
            label="Unit cost"
            hint="Recorded now so past usage never has to be back-filled with a guess."
          >
            <div className="svinv-input-group">
              <input
                type="number"
                min="0"
                step="0.01"
                value={itemForm.unitCost}
                onChange={(e) => setItemForm({ ...itemForm, unitCost: e.target.value })}
                placeholder="0.00"
              />
              <span className="suffix">
                per {UNIT_SUFFIX[itemForm.unit] || itemForm.unit}
              </span>
            </div>
          </Field>

          <Field label="Category">
            <input
              value={itemForm.category}
              onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
              placeholder="e.g. Colour"
            />
          </Field>

          <Field label="Supplier">
            <input
              value={itemForm.supplierName}
              onChange={(e) =>
                setItemForm({ ...itemForm, supplierName: e.target.value })
              }
              placeholder="Optional"
            />
          </Field>

          <Field label="Low stock alert at" hint="0 means no alert.">
            <div className="svinv-input-group">
              <input
                type="number"
                min="0"
                step="any"
                value={itemForm.lowStockThreshold}
                onChange={(e) =>
                  setItemForm({ ...itemForm, lowStockThreshold: e.target.value })
                }
                placeholder="0"
              />
              <span className="suffix">
                {UNIT_SUFFIX[itemForm.unit] || itemForm.unit}
              </span>
            </div>
          </Field>

          {/* ADDITIVE on create AND on edit. The same field in both places, because the
              purchase is one event: having to close the form and find a different button to
              record the same delivery is how "why is my item 0" happened in the first place. */}
          <Field
            label={editingItem ? "Add stock now" : "How many are you adding?"}
            span2
            hint={
              editingItem
                ? `Optional. This ADDS to the ${formatQty(editingTotal, itemForm.unit)} ${
                    UNIT_SUFFIX[itemForm.unit] || itemForm.unit
                  } you already have — it does not set the number. To bring a count DOWN, use Correct the count on the item: a correction is a different act and is recorded as one.`
                : itemForm.unit === "each"
                  ? "Optional. How many WHOLE units you are putting in now — 20 bottles of Detox is 20, not 1000. Leave it empty and the item starts at 0 until you add stock."
                  : `Optional. The TOTAL amount, not the number of containers — 10 bottles of 50 is ${formatQty(500, itemForm.unit)}. Leave it empty and the item starts at 0 until you add stock.`
            }
          >
            <div className="svinv-input-group">
              <input
                type="number"
                min="0"
                step={itemForm.unit === "each" ? "1" : "any"}
                value={itemForm.initialQuantity}
                onChange={(e) =>
                  setItemForm({ ...itemForm, initialQuantity: e.target.value })
                }
                placeholder="0"
              />
              <span className="suffix">
                {UNIT_SUFFIX[itemForm.unit] || itemForm.unit}
              </span>
            </div>
          </Field>

          {editingItem ? (
            <div className="span-2">
              <div className="svinv-choice-note">
                {editingItem.unit === "each" ? (
                  <>
                    <strong>Counted in whole units.</strong> Detox and Orielle are recorded as
                    &ldquo;each&rdquo; — the number is bottles, not millilitres. The unit cannot be
                    changed now: every quantity already recorded is expressed in it, so changing
                    it would silently reinterpret the history.
                  </>
                ) : (
                  <>
                    <strong>Counted in {UNIT_SUFFIX[editingItem.unit]}.</strong> The number is
                    the total volume, not the number of containers.
                  </>
                )}
              </div>
            </div>
          ) : null}

          <Field label="Notes" span2>
            <input
              value={itemForm.notes}
              onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })}
              placeholder="Optional"
            />
          </Field>
        </Modal>
      ) : null}

      {/* ---- Bulk import ---- */}
      {showImport ? (
        <Modal
          title="Import products from a CSV"
          subtitle="One product per row. Paste from a spreadsheet, or choose a file. Nothing is saved until you press Import."
          onClose={() => setShowImport(false)}
          // No submit button in this footer, so Enter inside the paste box must not reload the
          // page — the textarea takes newlines, and anything else is swallowed here.
          onSubmit={(event) => event.preventDefault()}
          wide
          footer={
            <div className="svinv-import-footer">
              <div className="svinv-import-counts">
                {importPreview.fatal
                  ? importPreview.fatal
                  : `${importReady} ready · ${importPreview.rows.length - importReady} with problems`}
              </div>
              <div className="svinv-modal-actions">
                <button
                  type="button"
                  className="svinv-btn ghost"
                  onClick={() => setShowImport(false)}
                  disabled={importBusy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="svinv-btn"
                  onClick={submitImport}
                  disabled={importBusy || Boolean(importPreview.fatal) || importReady === 0}
                >
                  {importBusy
                    ? "Importing…"
                    : `Import ${importReady || ""} product${importReady === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          }
        >
          <div className="svinv-import">
            {/*
              THE PREVIEW IS THE PAGE. Before anything is loaded there is a drop zone, a paste
              box and a scannable column reference; as soon as rows exist, those get out of the
              way and the preview takes over. The old version showed all of it at once, so the
              modal opened as a wall of prose above an empty box.
            */}
            {importPreview.rows.length === 0 && !importPreview.fatal ? (
              <>
                <div className="svinv-import-drop">
                  <div className="svinv-import-drop-main">
                    <strong>Drop a .csv here</strong>
                    <span>or</span>
                    <label className="svinv-btn ghost sm">
                      <input
                        type="file"
                        accept=".csv,text/csv,text/plain"
                        onChange={readImportFile}
                      />
                      Choose a file
                    </label>
                  </div>
                  <div className="svinv-import-drop-side">
                    <button
                      type="button"
                      className="svinv-link-btn"
                      onClick={downloadImportTemplate}
                    >
                      Download a template
                    </button>
                    <button
                      type="button"
                      className="svinv-link-btn"
                      onClick={() => onImportText(IMPORT_EXAMPLE_ROWS.join("\n"))}
                    >
                      Use an example
                    </button>
                  </div>
                </div>

                <table className="svinv-table svinv-import-columns">
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>What goes in it</th>
                      <th>Example</th>
                    </tr>
                  </thead>
                  <tbody>
                    {IMPORT_COLUMN_HELP.map((entry) => (
                      <tr key={entry.column}>
                        <td>
                          <code>{entry.column}</code>
                          {entry.required ? <span className="req"> *</span> : null}
                        </td>
                        <td>{entry.what}</td>
                        <td className="svinv-import-example">{entry.example}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}

            {importPreview.fatal ? (
              <div role="alert" className="svinv-notice error">
                {importPreview.fatal}
              </div>
            ) : null}

            <textarea
              className="svinv-import-text"
              value={importText}
              onChange={(e) => onImportText(e.target.value)}
              placeholder={IMPORT_EXAMPLE_ROWS.join("\n")}
              spellCheck={false}
              rows={importPreview.rows.length ? 3 : 4}
            />
            {importPreview.rows.length > 0 ? (
              <div className="svinv-import-preview">
                <table>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Name</th>
                      <th>Unit</th>
                      <th>Cost</th>
                      <th>Opening</th>
                      <th>Used</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.rows.map((row) => (
                      <tr
                        key={row.line}
                        className={row.errors.length ? "svinv-import-bad" : undefined}
                      >
                        <td>{row.line}</td>
                        <td>{row.name || "—"}</td>
                        <td>{row.unit}</td>
                        <td>{row.unitCost ? money(row.unitCost) : "—"}</td>
                        <td>{row.initialQuantity ? formatQty(row.initialQuantity, row.unit) : "—"}</td>
                        <td>{row.usageMode === "shared" ? "Shared container" : "Once per job"}</td>
                        <td>
                          {row.errors.length ? (
                            <span className="svinv-import-why">{row.errors.join("; ")}</span>
                          ) : (
                            <span className="svinv-import-ok">Ready</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="svinv-import-note">
                  Rows with problems are skipped — the rest still import. Fix a row and the table
                  updates as you type.
                </p>
              </div>
            ) : null}

            {importOutcome ? (
              <div className="svinv-notice ok">
                <strong>
                  {importOutcome.created} product{importOutcome.created === 1 ? "" : "s"} added
                </strong>
                {importOutcome.skipped ? `, ${importOutcome.skipped} row(s) skipped` : ""}.{" "}
                {importOutcome.failures.length ? (
                  <ul className="svinv-import-failures">
                    {importOutcome.failures.map((failure) => (
                      <li key={failure.line}>
                        Row {failure.line} ({failure.name}): {failure.message}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {/* ---- Allocate ---- */}
      {allocateItem ? (
        <Modal
          title={`Allocate ${allocateItem.name}`}
          subtitle="Send this to one location, or to several at once — each gets its own entry in the history, grouped as one dispatch."
          onClose={() => setAllocateItem(null)}
          onSubmit={submitAllocate}
          footer={
            <ModalActions
              busy={busy}
              disabled={allocateLocationIds.length === 0 || !allocateQty}
              onCancel={() => setAllocateItem(null)}
              confirmLabel={`Allocate to ${allocateLocationIds.length} location${
                allocateLocationIds.length === 1 ? "" : "s"
              }`}
            />
          }
        >
          <Field label="Locations" required span2>
            {locations.length === 0 ? (
              <span className="hint warn">
                No locations found for your business.
              </span>
            ) : (
              <>
                <div className="svinv-picker">
                  {locations.map((location) => (
                    <label className="svinv-picker-row" key={location._id}>
                      <input
                        type="checkbox"
                        checked={allocateLocationIds.includes(location._id)}
                        onChange={(e) =>
                          setAllocateLocationIds((current) =>
                            e.target.checked
                              ? [...current, location._id]
                              : current.filter((id) => id !== location._id),
                          )
                        }
                      />
                      <span>
                        <span className="name">{location.name}</span>
                        {location.city || location.state ? (
                          <span className="where">
                            {" "}
                            ·{" "}
                            {[location.city, location.state]
                              .filter(Boolean)
                              .join(", ")}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="svinv-picker-tools">
                  <button
                    type="button"
                    onClick={() =>
                      setAllocateLocationIds(locations.map((loc) => loc._id))
                    }
                  >
                    Select all
                  </button>
                  <button type="button" onClick={() => setAllocateLocationIds([])}>
                    Clear
                  </button>
                </div>
              </>
            )}
          </Field>

          <Field
            label="Quantity per location"
            required
            hint={`Each selected location receives this amount, in ${UNIT_SUFFIX[allocateItem.unit] || allocateItem.unit}.`}
          >
            <div className="svinv-input-group">
              <input
                type="number"
                min="0"
                step={allocateItem.unit === "each" ? "1" : "any"}
                value={allocateQty}
                onChange={(e) => setAllocateQty(e.target.value)}
                placeholder="0"
                required
              />
              <span className="suffix">
                {UNIT_SUFFIX[allocateItem.unit] || allocateItem.unit}
              </span>
            </div>
          </Field>

          <Field label="Notes" span2>
            <input
              value={allocateNotes}
              onChange={(e) => setAllocateNotes(e.target.value)}
              placeholder="e.g. delivery from supplier"
            />
          </Field>
        </Modal>
      ) : null}
      {/* ---- Used on a client (Phase B) ---- */}
      {useItem ? (
        <Modal
          title={
            useIsCountOnly
              ? `Correct the count — ${useItem.name}`
              : `Used on a client — ${useItem.name}`
          }
          subtitle={`Currently ${formatQty(useItem.quantity, useItem.unit)} ${
            useItem.unit === "each" ? "" : UNIT_SUFFIX[useItem.unit]
          } ${
            useItem.locationId === null
              ? isIndependent
                ? "in your stock"
                : "in the storeroom (not sent to a shop yet)"
              : "at this shop"
          }.`}
          onClose={() => setUseItem(null)}
          onSubmit={submitUse}
          footer={
            <ModalActions
              busy={busy}
              disabled={useQty === ""}
              onCancel={() => setUseItem(null)}
              confirmLabel={
                useMode === "consume"
                  ? "Record as used"
                  : useMode === "waste"
                    ? "Record as spilled or expired"
                    : useMode === "return"
                      ? "Hand back"
                      : "Correct the count"
              }
            />
          }
        >
          {/* CHOOSE FIRST, THEN FILL IN. The three were buttons that fired on click, so
              "Correct the count" posted a correction the instant you pressed it — before you
              had been asked what the count was. */}
          <Field
            label={useIsCountOnly ? "What needs correcting?" : "What happened?"}
            required
            span2
          >
            <div className="svinv-modes">
              {[
                // A store has no shelf, so "used" and "lost" cannot happen to it; and an R2
                // records no usage at all, because service is not theirs to record. Offering
                // either here would let the entry absorb work done somewhere else and leave
                // a location showing stock it never had.
                ...(useIsCountOnly
                  ? []
                  : [
                      {
                        id: "consume",
                        label: "Used on a client",
                        hint: "Product cost. Recorded as used.",
                      },
                      {
                        id: "waste",
                        label: "Lost or spilled",
                        hint: "Needs a reason. Kept separate from cost.",
                      },
                    ]),
                {
                  id: "adjust",
                  label: "The count is wrong",
                  hint: "You type what is on the shelf.",
                },
                // Only from a holding: you cannot hand back stock you were never given.
                ...(useFromHolding
                  ? [
                      {
                        id: "return",
                        label: "Hand back unused",
                        hint: "Puts it back on the shelf.",
                      },
                    ]
                  : []),
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`svinv-mode${useMode === option.id ? " selected" : ""}`}
                  aria-pressed={useMode === option.id}
                  onClick={() => setUseMode(option.id)}
                >
                  <strong>{option.label}</strong>
                  <span>{option.hint}</span>
                </button>
              ))}
            </div>
          </Field>

          <Field
            label={
              useMode === "adjust"
                ? "What is actually on the shelf?"
                : useMode === "waste"
                  ? "How much was lost?"
                  : useMode === "return"
                    ? "How much are you handing back?"
                    : "How much was used?"
            }
            required
            hint={
              useMode === "adjust"
                ? `Type the number you counted — NOT the difference. The change is worked out for you. The system currently records ${formatQty(useItem.quantity, useItem.unit)}.`
                : useMode === "return"
                  ? `Back onto the shelf. You are holding ${formatQty(useItem.quantity, useItem.unit)} ${useItem.unit === "each" ? "" : UNIT_SUFFIX[useItem.unit]}.`
                  : useItem.unit === "each"
                    ? "In the item's own unit. Shared bottle that is now empty? Put 1 — the cost lands on the period, not on one client, and nobody is asked to work out a cost per client."
                    : "In the item's own unit. Estimating a pour is fine; a cost per client is never an input, it is worked out later across all clients."
            }
          >
            <div className="svinv-input-group">
              <input
                type="number"
                min="0"
                step={useItem.unit === "each" ? "1" : "any"}
                value={useQty}
                onChange={(e) => setUseQty(e.target.value)}
                placeholder="0"
                autoFocus
                required
              />
              <span className="suffix">{UNIT_SUFFIX[useItem.unit]}</span>
            </div>
          </Field>

          <Field
            label="Reason"
            hint={
              useMode === "adjust"
                ? "Required for a correction — a count that moved with no explanation is the one thing this page cannot defend."
                : useMode === "waste"
                  ? "Required. This is where a loss hides."
                  : "Optional when it was used on a client."
            }
          >
            <input
              value={useReason}
              onChange={(e) => setUseReason(e.target.value)}
              placeholder={
                useMode === "adjust" ? "e.g. recount after stocktake" : "e.g. colour service"
              }
            />
          </Field>

          {/* In correction mode the number typed is the count, so the change it implies is
              shown BEFORE it is applied — the difference between "the shelf says 12" and
              "-28" must not be something the user has to work out. */}
          {useMode === "adjust" && useQty !== "" ? (
            <div className="span-2">
              <div className="svinv-choice-note">
                {Number(useQty) === Number(useItem.quantity) ? (
                  <>That is the count already recorded — nothing would change.</>
                ) : (
                  <>
                    That corrects the count from{" "}
                    <strong>{formatQty(useItem.quantity, useItem.unit)}</strong> to{" "}
                    <strong>{formatQty(Number(useQty), useItem.unit)}</strong> — a change of{" "}
                    <strong>
                      {Number(useQty) > Number(useItem.quantity) ? "+" : ""}
                      {formatQty(Number(useQty) - Number(useItem.quantity), useItem.unit)}
                    </strong>
                    {useItem.unit === "each" ? "" : ` ${UNIT_SUFFIX[useItem.unit]}`}.
                    {!canAdjust
                      ? " This will wait for the owner's approval."
                      : null}
                  </>
                )}
              </div>
            </div>
          ) : null}

          <div className="span-2">
            <div className="svinv-choice-note">
              {useIsCountOnly ? (
                <>
                  <strong>
                    {canRecordUsage
                      ? "The storeroom is where stock is kept, not where services happen."
                      : "Service usage is not recorded from the desk."}
                  </strong>{" "}
                  {canRecordUsage ? (
                    <>
                      Nothing is used out of it, so only its count can be wrong — and only its
                      count can be corrected. Record what a client used at the shop where the
                      work happened.
                      <br />
                      <br />
                      Sending stock to a shop does not use it up; it moves it. A spillage belongs
                      where it happened, not here.
                    </>
                  ) : (
                    <>
                      You hand product to the stylist who will use it, and that record names
                      them. From here you can correct the count — which the owner must approve —
                      or give the stock to the person taking it.
                    </>
                  )}
                </>
              ) : useMode === "adjust" && role === "R2" ? (
                <>
                  <strong>This will not change the shelf yet.</strong> A shop admin&apos;s
                  correction is recorded and the owner has to approve it — that is what stops a
                  count being quietly walked down.
                </>
              ) : useMode === "waste" ? (
                <>
                  <strong>Used and lost are separate numbers.</strong> A bottle that went off is
                  not product cost, and a report that mixed them could never tell you either.
                </>
              ) : useMode === "consume" ? (
                <>
                  <strong>This counts as product cost</strong> for the period. You do not need
                  to know a cost per client — nobody does; it is worked out later across all
                  clients.
                </>
              ) : null}
            </div>
          </div>
        </Modal>
      ) : null}
      {/* ---- Issue to a professional ---- */}
      {issueItem ? (
        <Modal
          title={`Give to a stylist — ${issueItem.name}`}
          subtitle={`${
            issueItem.locationId
              ? "Hands stock to a named person."
              : "Hands stock from the storeroom to a named person."
          } They will see it on their own page, which is what makes it checkable.`}
          onClose={() => setIssueItem(null)}
          onSubmit={submitIssue}
          footer={
            <ModalActions
              busy={busy}
              disabled={!issueStaffId || !issueQty}
              onCancel={() => setIssueItem(null)}
              confirmLabel="Give stock"
            />
          }
        >
          <Field
            label="Who is taking it?"
            required
            span2
            hint="Someone who works in your business. They will see it on their own page."
          >
            {staffOptions.length === 0 ? (
              <span className="hint warn">
                No professionals found for this business. Stock can only be given to an R3,
                R4 or R7 on your staff list.
              </span>
            ) : (
              <select
                value={issueStaffId}
                onChange={(e) => setIssueStaffId(e.target.value)}
                required
              >
                <option value="">Choose someone…</option>
                {staffOptions.map((member) => (
                  <option key={member._id} value={member._id}>
                    {member.name} ({member.role})
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field
            label="How much?"
            required
            hint={`In ${UNIT_SUFFIX[issueItem.unit] || issueItem.unit}. ${
              issueItem.locationId
                ? `Ready to use at this shop: ${formatQty(issueItem.quantity, issueItem.unit)}.`
                : "It goes straight from the storeroom. Send stock to a shop first if it should come off that shop's shelf."
            }`}
          >
            <div className="svinv-input-group">
              <input
                type="number"
                min="0"
                step={issueItem.unit === "each" ? "1" : "any"}
                value={issueQty}
                onChange={(e) => setIssueQty(e.target.value)}
                placeholder="0"
                autoFocus
                required
              />
              <span className="suffix">{UNIT_SUFFIX[issueItem.unit]}</span>
            </div>
          </Field>

          <Field label="Notes" span2>
            <input
              value={issueNotes}
              onChange={(e) => setIssueNotes(e.target.value)}
              placeholder="Optional — e.g. for Saturday's colour clients"
            />
          </Field>

          <div className="span-2">
            <div className="svinv-choice-note">
              <strong>Why this does not need the owner&apos;s approval.</strong> It reduces the
              shelf, but unlike a count correction it names a person — and a person can be asked
              about it. That is the difference between an accountable movement and an anonymous
              one.
            </div>
          </div>
        </Modal>
      ) : null}

      {/* ---- Use on an appointment ---- */}
      {apptUse ? (
        <Modal
          title="Used on a client"
          subtitle="Which clients was it used on? Pick one, or several — the same amount is recorded once per client, which is what makes product cost per service possible."
          onClose={() => setApptUse(null)}
          onSubmit={submitApptUse}
          footer={
            <ModalActions
              busy={busy}
              disabled={apptUse.appointmentRefs.length === 0}
              onCancel={() => setApptUse(null)}
              confirmLabel={
                apptUse.appointmentRefs.length > 1
                  ? `Record on ${apptUse.appointmentRefs.length} clients`
                  : "Record as used"
              }
            />
          }
        >
          <Field
            label="Which appointments?"
            required
            span2
            hint="Your own jobs only, from 30 days ago to a week ahead. Cancelled and no-show jobs are left out — no service was delivered, so nothing was used. Pick several to record the same product across them in one go."
          >
            {apptLoading ? (
              <span className="hint">Loading your appointments…</span>
            ) : apptOptions.length === 0 ? (
              <span className="hint warn">
                No appointments in that window. Record the use from the holding row instead —
                it will not be linked to a job, which only matters for per-service costing.
              </span>
            ) : (
              <div className="svinv-picker">
                {apptOptions.map((booking) => (
                  <label className="svinv-picker-row" key={booking._id}>
                    <input
                      type="checkbox"
                      checked={apptUse.appointmentRefs.includes(booking._id)}
                      onChange={() => toggleAppointment(booking._id)}
                    />
                    <span>
                      <strong>
                        {booking.appointmentDate} {booking.startTime}
                      </strong>{" "}
                      — {clientNameOf(booking)}{" "}
                      — {booking.serviceId?.name || "Service"}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </Field>

          {apptUse.appointmentRefs.length ? (
            <Field
              label="What did EACH job use?"
              span2
              hint={
                apptUse.sharedServiceId
                  ? apptDefaults.length
                    ? "Pre-filled with what this service used last time, recorded once per selected job. Change any that differ — it is a starting point, not a claim about what happened."
                    : "Nothing has been recorded for this service yet, so these start empty. It fills itself in from now on."
                  : "The selected jobs are for different services, so nothing could be pre-filled without guessing which one to use. Enter the amounts — they are recorded once per selected job."
              }
            >
              <div className="svinv-lines">
                {myHoldings
                  .filter((row) => row.locationId === apptUse.locationId)
                  .map((row) => (
                    <div className="svinv-line" key={row.itemId}>
                      <span className="svinv-line-name">
                        {row.name}
                        <span className="svinv-line-meta">
                          {formatQty(row.outstanding, row.unit)} left with you — this amount is
                          recorded per job, so {apptUse.appointmentRefs.length}{" "}
                          {apptUse.appointmentRefs.length === 1 ? "job uses" : "jobs use"}{" "}
                          {apptLines[row.itemId]
                            ? formatQty(
                                Number(apptLines[row.itemId]) *
                                  apptUse.appointmentRefs.length,
                                row.unit,
                              )
                            : 0}{" "}
                          in total
                        </span>
                      </span>
                      <div className="svinv-input-group">
                        <input
                          type="number"
                          min="0"
                          step={row.unit === "each" ? "1" : "any"}
                          value={apptLines[row.itemId] || ""}
                          onChange={(e) =>
                            setApptLines({
                              ...apptLines,
                              [row.itemId]: e.target.value,
                            })
                          }
                          placeholder="0"
                        />
                        <span className="suffix">{UNIT_SUFFIX[row.unit]}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </Field>
          ) : null}
        </Modal>
      ) : null}

      {/* ---- Shared container: open, ticked, finished ---- */}
      {bottlePanel ? (
        <Modal
          title={`${bottlePanel.name} — ${bottlePanel.isOpen ? "open" : "finished"}`}
          subtitle={
            bottlePanel.coveredCount
              ? bottlePanel.seesCost
                ? `One container, $${bottlePanel.containerCost.toFixed(2)}. It covers ${bottlePanel.coveredCount} job${bottlePanel.coveredCount === 1 ? "" : "s"}, so each carries $${bottlePanel.shareCost.toFixed(2)} of product.`
                : `One container. It covers ${bottlePanel.coveredCount} job${bottlePanel.coveredCount === 1 ? "" : "s"}, and its cost is divided between them.`
              : maySetContainerClients
                ? "No jobs on it yet. Tick the jobs it serves — nothing is divided until you finish it."
                : "No jobs on it yet. The professional using it adds the jobs it serves — nothing is divided until it is finished."
          }
          onClose={() => setBottlePanel(null)}
          onSubmit={(event) => {
            event.preventDefault();
            if (bottlePanel.isOpen) finishBottle(bottlePanel);
          }}
          footer={
            <>
              <button
                type="button"
                className="svinv-btn ghost"
                disabled={bottleSaving}
                onClick={() => setBottlePanel(null)}
              >
                Close
              </button>
              {bottlePanel.isOpen ? (
                maySetContainerClients ? (
                  <button type="submit" className="svinv-btn" disabled={bottleSaving}>
                    {bottleSaving ? "Working…" : "Container finished"}
                  </button>
                ) : null
              ) : maySetContainerClients ? (
                // A finished container's ticks are locked until the holder asks to change them.
                <button
                  type="button"
                  className={bottleEditing ? "svinv-btn ghost" : "svinv-btn"}
                  disabled={bottleSaving}
                  onClick={() => setBottleEditing((open) => !open)}
                >
                  {bottleEditing ? "Done updating" : "Update"}
                </button>
              ) : null}
            </>
          }
        >
          <Field
            label="Jobs this container covers"
            span2
            hint={
              !maySetContainerClients
                ? "Read only. The number of ticks is what divides this container's cost between jobs, so the jobs it covered are the professional's record — only the person who used it can change the list."
                : bottleReadOnly
                  ? "Read only — this container is finished and its cost is already in your figures. Press Update above if you need to change which jobs share it."
                  : "A tick, not an amount — the number of ticks IS the divisor. Ticking and unticking saves immediately, so nothing is lost if you close the screen. Each row is one job: its date, the client, and the service they had."
            }
          >
            {apptLoading ? (
              <span className="hint">Loading appointments…</span>
            ) : apptOptions.length === 0 ? (
              <span className="hint warn">
                No appointments came back for the person holding this container in the last 60
                days.
              </span>
            ) : (
              <div className="svinv-picker">
                {apptOptions.map((booking) => {
                  const ticked = bottlePanel.appointments.some(
                    (row) => String(row.bookingId) === String(booking._id),
                  );
                  return (
                    <label className="svinv-picker-row" key={booking._id}>
                      <input
                        type="checkbox"
                        checked={ticked}
                        disabled={bottleSaving || bottleReadOnly}
                        onChange={() => toggleBottleAppointment(bottlePanel, booking._id)}
                      />
                      <span>
                        <strong>
                          {booking.appointmentDate} {booking.startTime}
                        </strong>{" "}
                        — {clientNameOf(booking)}{" "}
                        — {booking.serviceId?.name || "Service"}
                        {/*
                          WHO PUT THIS JOB ON, when it was not the holder.

                          The number of ticks is the divisor, so every tick is a claim about work
                          that was delivered. The holder does not need a label for their own
                          action; somebody at the desk adding one for them is a claim about a room
                          they may not have been in, and it says so — which is what makes it a
                          delegation rather than a guess wearing no name.
                        */}
                        {ticked && coveredByOther(booking._id) ? (
                          <span className="svinv-delegated">
                            added by {coveredByOther(booking._id)} for the stylist
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            {/*
              ALREADY ON THIS CONTAINER, OUTSIDE THE WINDOW ABOVE.

              The list above is a window over the appointment book. Without this section a job ticked
              six months ago was counted but not shown, so an old container could not be corrected at
              all — and the subtitle could say "covers 20" above a list of twelve, which teaches the
              reader to distrust both numbers.
            */}
            {coveredOutsideWindow.length > 0 ? (
              <div style={{ marginTop: "16px" }}>
                <div className="svinv-sub-h">
                  Also on this container, before the list above
                </div>
                <p className="hint">
                  These jobs are older than the appointments shown above. Untick one to take it off
                  the container and the share is divided again without it.
                </p>
                <div className="svinv-picker">
                  {coveredOutsideWindow.map((row) => (
                    <label className="svinv-picker-row" key={row.bookingId}>
                      <input
                        type="checkbox"
                        checked
                        disabled={bottleSaving || bottleReadOnly}
                        onChange={() => toggleBottleAppointment(bottlePanel, row.bookingId)}
                      />
                      <span>
                        <strong>
                          {row.appointmentDate} {row.startTime}
                        </strong>{" "}
                        — {row.customerName || "Client"}{" "}
                        — {row.serviceName || "Service"}
                        {row.bookingMissing ? " (booking removed)" : ""}
                        {/*
                          WHO ACTED, when it was not the holder.

                          The number of ticks is the divisor, so each one is a claim about work that
                          was delivered. The holder needs no label for their own action; somebody at
                          the desk adding one for them is a claim about a room they may not have
                          been in — and it says so. **That label is what makes this a delegation
                          rather than the anonymous entry the rest of this feature refuses.**
                        */}
                        {row.delegated ? (
                          <span className="svinv-delegated">
                            added by {row.addedByName} for the stylist
                          </span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
          </Field>

          <div className="span-2">
            <div className="svinv-choice-note">
              {/*
                WHO FINISHED IT, when that was not the holder. Stated on the container rather than
                buried in a job row: finishing is the act that divides the money, so if somebody did
                it on the holder's behalf, that is the single most important thing on this panel for
                them to see.
              */}
              {bottlePanel.finishedBySomebodyElse ? (
                <p className="svinv-delegated-note">
                  <strong>Finished by {bottlePanel.finishedByName}</strong> for the stylist holding
                  it. Every job above still counts and can be changed — if one is missing, add it and
                  the share is divided again.
                </p>
              ) : null}
              {!bottlePanel.isOpen && bottlePanel.actingOnBehalf ? (
                <p className="svinv-delegated-note">
                  You are not the person who used this container. Anything you change here is
                  recorded against your name for them.
                </p>
              ) : null}
              {bottlePanel.isOpen ? (
                <>
                  <strong>Opening changed no stock.</strong> The container was already off the
                  shelf when it was issued. The one movement happens when you finish it, and that
                  is also when the cost is divided — so nothing is ever charged to a job before the
                  number of jobs is known.
                  {bottlePanel.coveredCount === 0 ? (
                    <>
                      {" "}
                      Finishing with nothing ticked is allowed, and its cost will show as{" "}
                      <strong>not charged to any service</strong> rather than disappearing.
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <strong>
                    {!maySetContainerClients
                      ? "Finished — yours to see, not to change."
                      : bottleReadOnly
                        ? "Finished — read only."
                        : "Updating."}
                  </strong>{" "}
                  {!maySetContainerClients ? (
                    <>
                      Its cost is divided across the appointments above, and the number of ticks is
                      what did the dividing. Only the professional who used this container can
                      change that list — it is a statement about work they delivered. Everything
                      here is visible to you so the figures can be checked.
                    </>
                  ) : bottleReadOnly ? (
                    <>
                      Its cost is divided across the appointments above, and those figures are
                      already in your reporting. Press <strong>Update</strong> to change which
                      clients share it; the share recalculates as you tick, and nothing needs saving.
                    </>
                  ) : (
                    <>
                      Its cost is divided across the appointments above. Tick or untick any of them
                      and the share recalculates — the list is the record, and nothing is stored
                      that could go stale. Press <strong>Done updating</strong> when you are finished.
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </Modal>
      ) : null}

      {/* ---- Add stock to the bucket ---- */}
      {receiveItem ? (
        <Modal
          title={`Add stock — ${receiveItem.name}`}
          subtitle={
            singleLocation
              ? `How much did you buy? Counted in ${UNIT_SUFFIX[receiveItem.unit]}. You have one location, so it can go straight there.`
              : `How much did you buy? Counted in ${UNIT_SUFFIX[receiveItem.unit]}. This adds to your own stock first — sending it to a location is a separate step.`
          }
          onClose={() => setReceiveItem(null)}
          onSubmit={receiveStock}
          footer={
            <ModalActions
              busy={busy}
              disabled={!receiveQty}
              onCancel={() => setReceiveItem(null)}
              confirmLabel={
                receiveSendToLocation && singleLocation
                  ? `Add and send to ${singleLocation.name}`
                  : "Add to my stock"
              }
            />
          }
        >
          <Field
            label="Quantity"
            required
            hint={
              receiveItem.packSize
                ? `One ${UNIT_SUFFIX[receiveItem.unit]} pack is ${formatQty(receiveItem.packSize, receiveItem.unit)} ${UNIT_SUFFIX[receiveItem.unit]} — enter the TOTAL, so a 1L bottle is 1000.`
                : "The total amount, not the number of packs."
            }
          >
            <div className="svinv-input-group">
              <input
                type="number"
                min="0"
                step={receiveItem.unit === "each" ? "1" : "any"}
                value={receiveQty}
                onChange={(e) => setReceiveQty(e.target.value)}
                placeholder="0"
                autoFocus
                required
              />
              <span className="suffix">{UNIT_SUFFIX[receiveItem.unit]}</span>
            </div>
          </Field>

          <div className="span-2">
            <div className="svinv-choice-note">
              {singleLocation && receiveSendToLocation ? (
                <>
                  <strong>Sent straight to your shop.</strong> This adds the stock to the
                  storeroom and then sends it to <strong>{singleLocation.name}</strong> — two
                  entries, in order, because that is what happened. It is only the second press
                  you are spared, for the one place it could go.
                  <br />
                  <br />
                  <label style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={receiveSendToLocation}
                      onChange={(e) => setReceiveSendToLocation(e.target.checked)}
                    />
                    <span>Leave it in my own stock instead.</span>
                  </label>
                </>
              ) : (
                <>
                  <strong>The two steps, in order:</strong>
                  <ol>
                    <li>
                      <strong>Add stock</strong> (this) — what you bought, now in your own
                      stock.
                    </li>
                    <li>
                      <strong>Send to shop</strong> — moves part of it to a shop where it
                      will be used.{locations.length > 1 ? " You have several, so you choose which." : ""}
                    </li>
                  </ol>
                </>
              )}
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );

  return <PageShell role={role}>{content}</PageShell>;
}

/**
 * THE "?" — an explanation you can ASK for, instead of one you must scroll past.
 *
 * This page carried a paragraph under every heading. A paragraph under every heading is read
 * once and then becomes wallpaper, and it makes the screen read as documentation of a model
 * rather than a tool for a job — which is exactly how it was received.
 *
 * The rule now: an explanation appears inline ONLY where somebody must choose between two
 * things that look alike, or where the screen is empty and they have no idea what to do next.
 * Everything else — the reasoning, the edge cases, why a number is measured the way it is —
 * lives in here, one tap away, and costs nothing to anyone who does not need it.
 */
function Why({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="svinv-why">
      <button
        type="button"
        className={`svinv-why-btn${open ? " open" : ""}`}
        aria-expanded={open}
        aria-label={open ? "Hide explanation" : "Explain this"}
        title={open ? "Hide explanation" : "Explain this"}
        onClick={() => setOpen((value) => !value)}
      >
        ?
      </button>
      {open ? <span className="svinv-why-text">{children}</span> : null}
    </span>
  );
}

/**
 * A field is a LABEL ABOVE a full-width control, never beside it.
 *
 * The first version let each input size itself, so a text input collapsed to its default
 * width and the modal read as a column of stubs. `min-width: 0` matters too: without it a
 * grid child refuses to shrink below its content and a long option label blows the column
 * out.
 */
function Field({ label, hint, required, span2, children }) {
  return (
    <div className={`svinv-field${span2 ? " span-2" : ""}`}>
      <span className="svinv-field-label">
        {label}
        {required ? <span className="req"> *</span> : null}
      </span>
      {children}
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

/**
 * The form IS the modal card, so Enter submits and the footer stays put while the body
 * scrolls — with twelve locations in the picker, a footer that scrolled away was the
 * difference between usable and not.
 */
function Modal({ title, subtitle, onClose, onSubmit, footer, children, wide }) {
  return (
    <div
      className="svinv-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <form
        className={`svinv-modal${wide ? " wide" : ""}`}
        onClick={(event) => event.stopPropagation()}
        onSubmit={onSubmit}
      >
        <div className="svinv-modal-head">
          <h3>{title}</h3>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        <div className="svinv-modal-body">{children}</div>
        <div className="svinv-modal-foot">{footer}</div>
      </form>
    </div>
  );
}

function ModalActions({ busy, disabled, onCancel, confirmLabel }) {
  return (
    <>
      <button type="button" className="svinv-btn ghost" onClick={onCancel}>
        Cancel
      </button>
      <button type="submit" className="svinv-btn" disabled={busy || disabled}>
        {busy ? "Working…" : confirmLabel}
      </button>
    </>
  );
}

export default ServiceInventoryPage;


